import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  BrainCircuit, TrendingUp, TrendingDown, Minus, AlertTriangle,
  Users, Eye, Search, Star, AlertCircle, CheckCircle, XCircle, Target,
  Activity, BookOpen, MessageSquare, RefreshCw, ChevronLeft, ArrowUpRight, ArrowDownRight,
  ChevronRight, ChevronsLeft, ChevronsRight, Plus, Trash2, Send, Calendar,
  Save, ClipboardCheck, Clock, Phone, Download, Globe2
} from 'lucide-react';
import { api } from '../services/api';
import ConfirmDialog from './ConfirmDialog';
import GuideUtilisationDrawer from './GuideUtilisationDrawer';
import { Toast } from './ui/Toast';
import { ECHELLE_TRANCHES, noteEnFrancais, seuilSurBareme, versEchelleTranches } from '../utils/baremeNotes';

// Le primaire se note sur 10, le collège et le lycée sur 20 (décision de
// Daniel, 7/10/2026). Le serveur donne le barème de chaque élève
// (`bareme_max`). Les seuils décisionnels et le seuil de stabilité sont réglés
// par l'école sur 20 : une moyenne du primaire y est ramenée (× 2) pour la
// catégorie, et le seuil de stabilité ramené au barème pour la tendance.
const baremeDe = (eleve) => Number(eleve?.bareme_max) || ECHELLE_TRANCHES;

// ============================================
// CONSTANTES PAR DÉFAUT (fallback si pas de config)
// ============================================

const ITEMS_PER_PAGE = 15;

const CATEGORIES_DEFAUT = [
  { id: 'excellence', label: 'Excellence', min: 16, max: 20, color: '#059669', bgColor: '#d1fae5', icon: Star },
  { id: 'tres_bien', label: 'Très Bien', min: 14, max: 15.99, color: '#1d4ed8', bgColor: '#dbeafe', icon: CheckCircle },
  { id: 'satisfaisant', label: 'Satisfaisant', min: 12, max: 13.99, color: '#ca8a04', bgColor: '#fef9c3', icon: Target },
  { id: 'alerte', label: 'Alerte', min: 10, max: 11.99, color: '#ea580c', bgColor: '#ffedd5', icon: AlertCircle },
  { id: 'difficulte', label: 'En difficulté', min: 0, max: 9.99, color: '#dc2626', bgColor: '#fee2e2', icon: XCircle },
];

// Mapping couleur/icône par id de catégorie
const CATEGORIES_STYLE = {
  excellence: { color: '#059669', bgColor: '#d1fae5', icon: Star },
  tres_bien: { color: '#1d4ed8', bgColor: '#dbeafe', icon: CheckCircle },
  satisfaisant: { color: '#ca8a04', bgColor: '#fef9c3', icon: Target },
  alerte: { color: '#ea580c', bgColor: '#ffedd5', icon: AlertCircle },
  difficulte: { color: '#dc2626', bgColor: '#fee2e2', icon: XCircle },
};

const SYNTHESE_STYLE = {
  faible: { label: 'Faible', color: '#059669', bgColor: '#d1fae5', icon: CheckCircle },
  modere: { label: 'Modere', color: '#ca8a04', bgColor: '#fef3c7', icon: AlertCircle },
  eleve: { label: 'Eleve', color: '#ea580c', bgColor: '#ffedd5', icon: AlertTriangle },
  critique: { label: 'Critique', color: '#dc2626', bgColor: '#fee2e2', icon: XCircle },
};

const DECISION_DIRECTION_OPTIONS = [
  { value: 'encouragement', label: 'Encouragement' },
  { value: 'suivi_simple', label: 'Suivi simple' },
  { value: 'plan_soutien', label: 'Plan de soutien' },
  { value: 'convocation_parents', label: 'Convocation parents' },
  { value: 'avertissement', label: 'Avertissement' },
  { value: 'conseil_discipline', label: 'Conseil de discipline' },
  { value: 'redoublement', label: 'Redoublement' },
  { value: 'passage_conditionnel', label: 'Passage conditionnel' },
  { value: 'autre', label: 'Autre' },
];

const DECISION_SOURCE_LABELS = {
  synthese: 'Synthese',
  vie_scolaire: 'Vie scolaire',
  matiere: 'Matiere',
  absence: 'Absence',
  comportement: 'Comportement',
  autre: 'Autre',
};

const SMS_STATUS_STYLE = {
  en_attente: { label: 'En attente', className: 'bg-amber-50 text-amber-700' },
  envoye: { label: 'Envoye', className: 'bg-sky-50 text-sky-700' },
  livre: { label: 'Livre', className: 'bg-emerald-50 text-emerald-700' },
  echec: { label: 'Echec', className: 'bg-red-50 text-red-700' },
};

const DEFAULT_ACTION_FORM = {
  decision_direction_id: '',
  type: 'observation',
  description: '',
  parent_id: '',
  contact_message: '',
};

const ACTION_TYPE_OPTIONS = [
  { value: 'convocation', label: 'Convocation', color: '#dc2626', bg: '#fee2e2' },
  { value: 'recommandation', label: 'Recommandation', color: '#ea580c', bg: '#ffedd5' },
  { value: 'observation', label: 'Observation', color: '#7c3aed', bg: '#ede9fe' },
  { value: 'sanction', label: 'Sanction', color: '#b91c1c', bg: '#fecaca' },
  { value: 'contact_parent', label: 'Contact parent', color: '#0284c7', bg: '#e0f2fe' },
];

const DEFAULT_PLAN_FORM = {
  decision_direction_id: '',
  objectif: '',
  responsable_nom: '',
  echeance: '',
  priorite: 'normale',
  statut: 'a_faire',
  resultats_attendus: '',
  commentaire: '',
};

const PLAN_PRIORITY_OPTIONS = [
  { value: 'basse', label: 'Basse', className: 'bg-slate-50 text-slate-600 border-slate-200' },
  { value: 'normale', label: 'Normale', className: 'bg-sky-50 text-sky-700 border-sky-200' },
  { value: 'haute', label: 'Haute', className: 'bg-red-50 text-red-700 border-red-200' },
];

const PLAN_STATUS_STYLE = {
  a_faire: { label: 'A faire', className: 'bg-slate-100 text-slate-700' },
  en_cours: { label: 'En cours', className: 'bg-amber-50 text-amber-700' },
  termine: { label: 'Termine', className: 'bg-emerald-50 text-emerald-700' },
  annule: { label: 'Annule', className: 'bg-gray-100 text-gray-500' },
};

const PLAN_STATUS_OPTIONS = [
  { value: 'a_faire', label: 'A faire' },
  { value: 'en_cours', label: 'En cours' },
  { value: 'termine', label: 'Termine' },
  { value: 'annule', label: 'Annule' },
];

const A_TRAITER_STYLES = {
  vie_scolaire_urgente: { icon: AlertTriangle, color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
  decision_sans_plan: { icon: ClipboardCheck, color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  plan_retard: { icon: Clock, color: '#b91c1c', bg: '#fef2f2', border: '#fecaca' },
  plan_echeance: { icon: Calendar, color: '#0284c7', bg: '#f0f9ff', border: '#bae6fd' },
  suivi_sans_action: { icon: BookOpen, color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
  contact_parent: { icon: Phone, color: '#0f766e', bg: '#f0fdfa', border: '#99f6e4' },
};

const SEVERITY_LABELS = {
  critical: 'Urgent',
  warning: 'A suivre',
  info: 'Info',
};

const getSyntheseStyle = (niveau) => SYNTHESE_STYLE[niveau] || SYNTHESE_STYLE.faible;

const getMainDecisionDirection = (decisions = []) => (
  decisions.find((decision) => decision?.statut !== 'cloturee') || decisions[0] || null
);

const DECISIONS_SANS_SUIVI_EXCLUES = new Set(['encouragement']);
const DECISIONS_PRIORITE_HAUTE = new Set(['avertissement', 'convocation_parents', 'conseil_discipline']);
const decisionNeedsFollowUp = (decision) => (
  decision?.statut !== 'cloturee' && !DECISIONS_SANS_SUIVI_EXCLUES.has(decision?.decision_finale)
);

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const OutilDecisionnel = () => {
  const pageTopRef = useRef(null);
  const resultsRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vue, setVue] = useState('dashboard');
  const [activeYear, setActiveYear] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Config dynamique
  const [categoriesConfig, setCategoriesConfig] = useState(CATEGORIES_DEFAUT);
  const [seuilStabilite, setSeuilStabilite] = useState(0.5);

  const [classesParSection, setClassesParSection] = useState({ primaire: [], college: [], lycee: [] });
  const [selectedClasse, setSelectedClasse] = useState('');
  const [selectedClasseNom, setSelectedClasseNom] = useState('');
  const [classeSearchTerm, setClasseSearchTerm] = useState('');
  const [isClasseDropdownOpen, setIsClasseDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategorie, setFilterCategorie] = useState('');
  const [filterTendance, setFilterTendance] = useState('');

  const [elevesData, setElevesData] = useState([]);
  const [aTraiter, setATraiter] = useState(null);
  const [aTraiterLoading, setATraiterLoading] = useState(false);
  const [activeATraiterTab, setActiveATraiterTab] = useState('all');
  const [aTraiterScope, setATraiterScope] = useState('classe');
  const [ficheEleve, setFicheEleve] = useState(null);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);
  const [isCheckingChanges, setIsCheckingChanges] = useState(false);
  const [toastInfo, setToastInfo] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [ficheLoadingEleveId, setFicheLoadingEleveId] = useState(null);

  // Journal de suivi
  const [journalActions, setJournalActions] = useState([]);
  const [journalLoading, setJournalLoading] = useState(false);
  const [showJournalForm, setShowJournalForm] = useState(false);
  const [newAction, setNewAction] = useState(DEFAULT_ACTION_FORM);
  const [journalSubmitting, setJournalSubmitting] = useState(false);
  const [parentsEleve, setParentsEleve] = useState([]);
  const [communicationsParents, setCommunicationsParents] = useState([]);
  const [smsConfig, setSmsConfig] = useState({ configured: false, missing: [] });
  const [smsSending, setSmsSending] = useState(false);
  const [plansAccompagnement, setPlansAccompagnement] = useState([]);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [planForm, setPlanForm] = useState(DEFAULT_PLAN_FORM);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [planSubmitting, setPlanSubmitting] = useState(false);
  const [planCompletingId, setPlanCompletingId] = useState(null);

  const [decisionForm, setDecisionForm] = useState({
    portee: 'globale',
    source_type: 'synthese',
    source_id: '',
    titre_probleme: '',
    decision_finale: 'suivi_simple',
    commentaire_direction: '',
  });
  const [decisionSaving, setDecisionSaving] = useState(false);
  const [decisionClosingId, setDecisionClosingId] = useState(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [decisionSourceContext, setDecisionSourceContext] = useState(null);
  const [decisionCloseComment, setDecisionCloseComment] = useState('');

  const scrollToTop = useCallback(() => {
    window.requestAnimationFrame(() => {
      const startNode = pageTopRef.current;
      if (!startNode) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      let parent = startNode.parentElement;
      while (parent) {
        const style = window.getComputedStyle(parent);
        const canScroll = ['auto', 'scroll'].includes(style.overflowY) && parent.scrollHeight > parent.clientHeight;
        if (canScroll) {
          parent.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        parent = parent.parentElement;
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, []);

  const scrollToResults = useCallback(() => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }, []);

  // ============================================
  // HELPERS (utilisent la config dynamique)
  // ============================================

  const getCategorie = useCallback((moyenne, baremeMax = ECHELLE_TRANCHES) => {
    const val = versEchelleTranches(parseFloat(moyenne) || 0, baremeMax);
    const found = categoriesConfig.find(c => val >= c.min && val <= c.max);
    if (found) {
      const style = CATEGORIES_STYLE[found.id] || { color: '#6b7280', bgColor: '#f3f4f6', icon: Target };
      return { ...found, ...style };
    }
    return { ...CATEGORIES_DEFAUT[CATEGORIES_DEFAUT.length - 1], ...CATEGORIES_STYLE.difficulte };
  }, [categoriesConfig]);

  const getTendance = useCallback((moyennes, baremeMax = ECHELLE_TRANCHES) => {
    if (!moyennes || moyennes.length < 2) return { type: 'insuffisant', label: 'Données insuffisantes' };
    const derniere = moyennes[moyennes.length - 1];
    const avantDerniere = moyennes[moyennes.length - 2];
    const diff = derniere - avantDerniere;
    const seuil = seuilSurBareme(seuilStabilite, baremeMax);
    if (diff > seuil) return { type: 'progression', label: 'En progression', icon: TrendingUp, color: '#059669', diff: `+${diff.toFixed(2)}` };
    if (diff < -seuil) return { type: 'regression', label: 'En régression', icon: TrendingDown, color: '#dc2626', diff: diff.toFixed(2) };
    return { type: 'stable', label: 'Stable', icon: Minus, color: '#ca8a04', diff: diff.toFixed(2) };
  }, [seuilStabilite]);

  const formatMoyenne = (val) => {
    if (!val && val !== 0) return '-';
    return parseFloat(val).toFixed(2);
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const showToast = useCallback((message, type = 'success') => {
    setToastInfo({ message, type });
  }, []);

  const getDecisionLabel = useCallback((decisionId) => {
    const decision = (ficheEleve?.decisions_direction || []).find(item => Number(item.id) === Number(decisionId));
    return decision?.titre_probleme || decision?.decision_finale_label || 'la situation de votre enfant';
  }, [ficheEleve]);

  const buildSmsMessage = useCallback((decisionId = '') => {
    const eleveNom = ficheEleve?.eleve
      ? `${ficheEleve.eleve.prenom || ''} ${ficheEleve.eleve.nom || ''}`.trim()
      : 'votre enfant';
    const sujet = decisionId ? getDecisionLabel(decisionId) : 'son suivi scolaire';
    return `Bonjour, la direction souhaite echanger avec vous concernant ${eleveNom} (${sujet}). Merci de contacter l etablissement.`;
  }, [ficheEleve, getDecisionLabel]);

  const resetActionForm = useCallback(() => {
    setNewAction({
      ...DEFAULT_ACTION_FORM,
      parent_id: parentsEleve[0]?.id || '',
    });
  }, [parentsEleve]);

  const resetPlanForm = useCallback((decisionId = '') => {
    setPlanForm({
      ...DEFAULT_PLAN_FORM,
      decision_direction_id: decisionId || '',
    });
    setEditingPlanId(null);
  }, []);

  const getDefaultDecisionForm = useCallback((fiche = null) => ({
    portee: 'globale',
    source_type: 'synthese',
    source_id: '',
    titre_probleme: '',
    decision_finale: fiche?.synthese_decisionnelle?.decision_suggeree || 'suivi_simple',
    commentaire_direction: '',
  }), []);

  const buildDecisionSourceFromItem = useCallback((item) => {
    if (!item || item.type !== 'vie_scolaire_urgente') return null;
    return {
      source_type: 'vie_scolaire',
      source_id: item.id,
      title: item.title || 'Evenement Vie scolaire',
      description: item.description || '',
      severity: item.badge || item.severity || '',
      date: item.date || null,
      eleve_nom: item.eleve_nom || '',
      classe_nom: item.classe_nom || '',
    };
  }, []);

  const buildDecisionSourceFromEvent = useCallback((event, fiche = null) => {
    if (!event?.id) return null;
    const eleveNom = fiche?.eleve ? `${fiche.eleve.prenom || ''} ${fiche.eleve.nom || ''}`.trim() : '';
    return {
      source_type: 'vie_scolaire',
      source_id: event.id,
      title: event.titre || event.type || 'Evenement Vie scolaire',
      description: event.description || '',
      severity: event.gravite || '',
      date: event.date_evenement || null,
      eleve_nom: eleveNom,
      classe_nom: fiche?.eleve?.classe_nom || '',
    };
  }, []);

  const buildDecisionFormFromSource = useCallback((fiche, source) => ({
    ...getDefaultDecisionForm(fiche),
    portee: 'ciblee',
    source_type: source?.source_type || 'vie_scolaire',
    source_id: source?.source_id || '',
    titre_probleme: source?.title || 'Evenement Vie scolaire',
    commentaire_direction: source?.description
      ? `Point de depart Vie scolaire : ${source.description}`
      : '',
  }), [getDefaultDecisionForm]);

  // ============================================
  // CHARGEMENT
  // ============================================

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const anneeResult = await api.anneesScolaires.getActive();
        if (anneeResult.success && anneeResult.data) setActiveYear(anneeResult.data);

        const classesResult = await api.classes.getAll();
        const classes = classesResult.success ? classesResult.data : classesResult;
        setClassesParSection({
          primaire: classes.filter(c => c.section_nom?.toLowerCase() === 'primaire'),
          college: classes.filter(c => ['collège', 'college'].includes(c.section_nom?.toLowerCase())),
          lycee: classes.filter(c => ['lycée', 'lycee'].includes(c.section_nom?.toLowerCase())),
        });

        // Charger config seuils
        try {
          const configResult = await api.scolarite.getConfigSeuilsDecisionnels();
          if (configResult.success && configResult.data) {
            if (configResult.data.categories?.length > 0) {
              // Enrichir avec les styles visuels
              const enriched = configResult.data.categories.map(cat => {
                const style = CATEGORIES_STYLE[cat.id] || { color: '#6b7280', bgColor: '#f3f4f6', icon: Target };
                return { ...cat, ...style };
              });
              setCategoriesConfig(enriched);
            }
            if (configResult.data.seuil_stabilite !== undefined) {
              setSeuilStabilite(configResult.data.seuil_stabilite);
            }
          }
        } catch (e) {
          console.log('📌 Config seuils non disponible, utilisation des défauts');
        }

      } catch (err) { console.error('Erreur chargement initial:', err); }
    };
    loadInitialData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-outil-decisionnel')) setIsClasseDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const chargerDonnees = useCallback(async (options = {}) => {
    if (!activeYear) return;
    const isGlobalATraiter = aTraiterScope === 'global';
    if (!selectedClasse && !isGlobalATraiter) return;

    const silent = Boolean(options.silent);
    if (!silent && selectedClasse) setLoading(true);
    setATraiterLoading(true);
    setError(null);

    const applyATraiterResult = (aTraiterResult) => {
      if (aTraiterResult.success) {
        const nextATraiter = aTraiterResult.data || null;
        setATraiter(nextATraiter);
        const sections = nextATraiter?.sections || [];
        setActiveATraiterTab(prev => (
          prev === 'all' || sections.some(section => section.key === prev) ? prev : 'all'
        ));
      } else {
        setATraiter(null);
      }
    };

    try {
      const aTraiterClasseId = isGlobalATraiter ? 'all' : selectedClasse;

      if (selectedClasse) {
        const [result, aTraiterResult] = await Promise.all([
          api.scolarite.getOutilDecisionnelClasse(selectedClasse, activeYear.id),
          api.scolarite.getOutilDecisionnelATraiter(aTraiterClasseId, activeYear.id),
        ]);
        if (result.success) {
          setElevesData(result.data || []);
          setLastRefreshAt(new Date().toISOString());
        }
        else { setError(result.error || 'Erreur'); setElevesData([]); }
        applyATraiterResult(aTraiterResult);
      } else {
        const aTraiterResult = await api.scolarite.getOutilDecisionnelATraiter('all', activeYear.id);
        applyATraiterResult(aTraiterResult);
        setLastRefreshAt(new Date().toISOString());
      }
    } catch (err) { setError('Erreur lors du chargement'); setElevesData([]); }
    finally {
      setATraiterLoading(false);
      if (!silent && selectedClasse) setLoading(false);
    }
  }, [selectedClasse, activeYear, aTraiterScope]);

  useEffect(() => { chargerDonnees(); }, [chargerDonnees]);

  const chargerFicheEleve = useCallback(async (eleveId, options = {}) => {
    if (!activeYear) return;
    const silent = Boolean(options.silent);
    if (!silent) setFicheLoadingEleveId(eleveId);
    try {
      const result = await api.scolarite.getOutilDecisionnelEleve(eleveId, activeYear.id);
      if (result.success) {
        const fiche = result.data || null;
        setFicheEleve(fiche);
        setLastRefreshAt(new Date().toISOString());
        if (fiche) {
          const sourceContext = options.decisionSource || null;
          setDecisionSourceContext(sourceContext);
          setDecisionForm(sourceContext
            ? buildDecisionFormFromSource(fiche, sourceContext)
            : getDefaultDecisionForm(fiche)
          );
          const parents = fiche.parents || [];
          const decisions = fiche.decisions_direction || [];
          setParentsEleve(parents);
          setCommunicationsParents(fiche.communications_parents || []);
          setSmsConfig(fiche.sms_config || { configured: false, missing: [] });
          setPlansAccompagnement(fiche.plans_accompagnement || []);
          resetPlanForm(decisions.find(decision => decision.statut !== 'cloturee')?.id || '');
          setNewAction(prev => ({
            ...DEFAULT_ACTION_FORM,
            parent_id: parents[0]?.id || '',
            decision_direction_id: decisions.find(decision => decision.statut !== 'cloturee')?.id || '',
            type: prev.type || 'observation',
          }));
        } else {
          setParentsEleve([]);
          setCommunicationsParents([]);
          setSmsConfig({ configured: false, missing: [] });
          setPlansAccompagnement([]);
          setDecisionSourceContext(null);
          resetPlanForm();
          setNewAction(DEFAULT_ACTION_FORM);
        }
        setVue('fiche');
        if (!silent) scrollToTop();
      }
      else setError('Erreur chargement fiche');
    } catch (err) { setError('Erreur chargement fiche'); }
    finally { if (!silent) setFicheLoadingEleveId(null); }

    // Charger le journal en parallèle
    try {
      const journalResult = await api.scolarite.getSuiviActions(eleveId, activeYear.id);
      if (journalResult.success) setJournalActions(journalResult.data || []);
      else setJournalActions([]);
    } catch { setJournalActions([]); }

    setShowJournalForm(false);
    setShowPlanForm(false);
  }, [activeYear, buildDecisionFormFromSource, getDefaultDecisionForm, resetPlanForm, scrollToTop]);

  const ouvrirFicheDepuisATraiter = useCallback((item) => {
    const source = buildDecisionSourceFromItem(item);
    chargerFicheEleve(item.eleve_id, source ? { decisionSource: source } : {});
    if (source) {
      showToast('Decision ciblee preparee depuis Vie scolaire.', 'info');
    }
  }, [buildDecisionSourceFromItem, chargerFicheEleve, showToast]);

  const preparerDecisionDepuisEvenement = useCallback((event) => {
    if (!ficheEleve) return;
    const source = buildDecisionSourceFromEvent(event, ficheEleve);
    if (!source) return;
    setDecisionSourceContext(source);
    setDecisionForm(buildDecisionFormFromSource(ficheEleve, source));
    showToast('Decision ciblee preparee depuis cet evenement.', 'info');
  }, [buildDecisionFormFromSource, buildDecisionSourceFromEvent, ficheEleve, showToast]);

  const resetDecisionForm = useCallback(() => {
    setDecisionSourceContext(null);
    setDecisionForm(getDefaultDecisionForm(ficheEleve));
  }, [ficheEleve, getDefaultDecisionForm]);

  const ouvrirPlanPourDecision = useCallback((decision, options = {}) => {
    if (!decision?.id) return;
    const titre = decision.titre_probleme || decision.decision_finale_label || 'decision direction';
    setPlanForm({
      ...DEFAULT_PLAN_FORM,
      decision_direction_id: decision.id,
      objectif: options.prefill === false ? '' : `Assurer le suivi de la decision : ${titre}`,
      priorite: DECISIONS_PRIORITE_HAUTE.has(decision.decision_finale) ? 'haute' : 'normale',
      statut: 'a_faire',
    });
    setEditingPlanId(null);
    setShowPlanForm(true);
    if (!options.silent) showToast('Plan pret a completer pour cette decision.', 'info');
  }, [showToast]);

  const ajouterAction = useCallback(async () => {
    if (!ficheEleve?.eleve?.id || !activeYear) return;

    const isContactParent = newAction.type === 'contact_parent';
    const parent = parentsEleve.find(item => Number(item.id) === Number(newAction.parent_id));
    const contactMessage = (newAction.contact_message || buildSmsMessage(newAction.decision_direction_id)).trim();
    const description = isContactParent
      ? [
        `Parent: ${parent?.nom_prenom || 'Non renseigne'}`,
        `Canal: SMS`,
        `Statut: ${smsConfig?.configured ? 'SMS envoye via OVH' : 'Contact prevu - OVH non configure'}`,
        `Message: ${contactMessage}`,
      ].join('\n')
      : newAction.description.trim();

    if (!isContactParent && !description) return;
    if (isContactParent && !parent) {
      showToast('Selectionne un parent avant d enregistrer le contact', 'error');
      return;
    }
    if (isContactParent && !contactMessage) {
      showToast('Le message au parent est requis', 'error');
      return;
    }

    const saveAction = async () => {
      const result = await api.scolarite.addSuiviAction({
        eleve_id: ficheEleve.eleve.id,
        annee_scolaire_id: activeYear.id,
        decision_direction_id: newAction.decision_direction_id || null,
        type: newAction.type,
        description,
      });
      if (result.success) {
        setJournalActions(prev => [result.data, ...prev]);
        resetActionForm();
        setShowJournalForm(false);
        showToast(isContactParent ? 'Contact parent enregistre dans le suivi' : 'Suivi ajoute avec succes', 'success');
        return true;
      }
      showToast(result.error || 'Erreur ajout action', 'error');
      return false;
    };

    const sendThenSave = async () => {
      setJournalSubmitting(true);
      setSmsSending(true);
      try {
        const result = await api.scolarite.sendParentCommunication({
          eleve_id: ficheEleve.eleve.id,
          annee_scolaire_id: activeYear.id,
          decision_direction_id: newAction.decision_direction_id || null,
          parent_id: parent.id,
          canal: 'sms',
          message: contactMessage,
        });

        if (result.success) {
          setCommunicationsParents(prev => [result.data, ...prev]);
          resetActionForm();
          setShowJournalForm(false);
          showToast('SMS envoye et suivi enregistre', 'success');
          return true;
        }

        if (result.data) {
          setCommunicationsParents(prev => [result.data, ...prev]);
        }
        showToast(result.error || 'Erreur lors de l envoi SMS', 'error');
        return false;
      } catch (err) {
        showToast('Erreur lors de l envoi SMS', 'error');
        return false;
      } finally {
        setSmsSending(false);
        setJournalSubmitting(false);
      }
    };

    if (isContactParent && smsConfig?.configured) {
      if (!parent.telephone_normalise) {
        showToast('Ce parent n a pas de numero SMS valide', 'error');
        return;
      }
      setConfirmDialog({
        title: 'Envoyer ce SMS ?',
        description: `Le SMS sera envoye a ${parent.nom_prenom} sur ${parent.telephone_normalise}.`,
        confirmText: 'Envoyer',
        type: 'question',
        onConfirm: sendThenSave,
      });
      return;
    }

    setJournalSubmitting(true);
    try {
      await saveAction();
    } catch (err) {
      showToast('Erreur lors de l ajout', 'error');
    } finally {
      setJournalSubmitting(false);
    }
  }, [ficheEleve, activeYear, newAction, parentsEleve, smsConfig, buildSmsMessage, resetActionForm, showToast]);

  const supprimerAction = useCallback(async (actionId) => {
    setConfirmDialog({
      title: 'Supprimer cette entree ?',
      description: 'Cette entree sera retiree du suivi decisionnel.',
      confirmText: 'Supprimer',
      type: 'warning',
      onConfirm: async () => {
        try {
          const result = await api.scolarite.deleteSuiviAction(actionId);
          if (result.success) {
            setJournalActions(prev => prev.filter(a => a.id !== actionId));
            showToast('Entree supprimee avec succes', 'success');
            return true;
          }
          showToast(result.error || 'Erreur suppression', 'error');
          return false;
        } catch (err) {
          showToast('Erreur lors de la suppression', 'error');
          return false;
        }
      },
    });
  }, [showToast]);

  const enregistrerPlanAccompagnement = useCallback(async () => {
    if (!ficheEleve?.eleve?.id || !activeYear || !planForm.objectif.trim()) return;

    setPlanSubmitting(true);
    try {
      const payload = {
        eleve_id: ficheEleve.eleve.id,
        annee_scolaire_id: activeYear.id,
        decision_direction_id: planForm.decision_direction_id || null,
        objectif: planForm.objectif.trim(),
        responsable_nom: planForm.responsable_nom.trim() || null,
        echeance: planForm.echeance || null,
        priorite: planForm.priorite,
        statut: planForm.statut,
        resultats_attendus: planForm.resultats_attendus.trim() || null,
        commentaire: planForm.commentaire.trim() || null,
      };

      const result = editingPlanId
        ? await api.scolarite.updatePlanAccompagnementDecision(editingPlanId, payload)
        : await api.scolarite.createPlanAccompagnementDecision(payload);

      if (result.success) {
        const savedPlan = result.data;
        setPlansAccompagnement(prev => (
          editingPlanId
            ? prev.map(plan => (plan.id === savedPlan.id ? savedPlan : plan))
            : [savedPlan, ...prev]
        ));
        setFicheEleve(prev => prev ? {
          ...prev,
          plans_accompagnement: editingPlanId
            ? (prev.plans_accompagnement || []).map(plan => (plan.id === savedPlan.id ? savedPlan : plan))
            : [savedPlan, ...(prev.plans_accompagnement || [])],
        } : prev);
        resetPlanForm(planForm.decision_direction_id);
        setShowPlanForm(false);
        showToast(editingPlanId ? 'Plan modifie avec succes' : 'Plan d accompagnement ajoute', 'success');
        return;
      }

      showToast(result.error || 'Erreur lors de l enregistrement du plan', 'error');
    } catch (err) {
      showToast('Erreur lors de l enregistrement du plan', 'error');
    } finally {
      setPlanSubmitting(false);
    }
  }, [ficheEleve, activeYear, planForm, editingPlanId, resetPlanForm, showToast]);

  const modifierPlanAccompagnement = useCallback((plan) => {
    if (!plan || plan.statut === 'termine') return;
    setPlanForm({
      decision_direction_id: plan.decision_direction_id || '',
      objectif: plan.objectif || '',
      responsable_nom: plan.responsable_nom || '',
      echeance: plan.echeance ? String(plan.echeance).slice(0, 10) : '',
      priorite: plan.priorite || 'normale',
      statut: plan.statut || 'a_faire',
      resultats_attendus: plan.resultats_attendus || '',
      commentaire: plan.commentaire || '',
    });
    setEditingPlanId(plan.id);
    setShowPlanForm(true);
  }, []);

  const remplacerPlanLocal = useCallback((updatedPlan) => {
    setPlansAccompagnement(prev => prev.map(plan => (plan.id === updatedPlan.id ? updatedPlan : plan)));
    setFicheEleve(prev => prev ? {
      ...prev,
      plans_accompagnement: (prev.plans_accompagnement || []).map(plan => (plan.id === updatedPlan.id ? updatedPlan : plan)),
    } : prev);
  }, []);

  const terminerPlanAccompagnement = useCallback(async (plan) => {
    if (!plan?.id) return;
    setConfirmDialog({
      title: 'Terminer ce plan ?',
      description: 'Le plan restera visible dans l historique avec la personne qui l a termine.',
      confirmText: 'Terminer',
      type: 'success',
      onConfirm: async () => {
        setPlanCompletingId(plan.id);
        try {
          const result = await api.scolarite.terminerPlanAccompagnementDecision(plan.id, {
            commentaire: plan.commentaire || null,
          });
          if (result.success) {
            remplacerPlanLocal(result.data);
            showToast('Plan termine avec succes', 'success');
            return true;
          }
          showToast(result.error || 'Erreur lors de la cloture du plan', 'error');
          return false;
        } catch (err) {
          showToast('Erreur lors de la cloture du plan', 'error');
          return false;
        } finally {
          setPlanCompletingId(null);
        }
      },
    });
  }, [remplacerPlanLocal, showToast]);

  const supprimerPlanAccompagnement = useCallback(async (planId) => {
    setConfirmDialog({
      title: 'Supprimer ce plan ?',
      description: 'Ce plan d accompagnement sera retire de la fiche decisionnelle.',
      confirmText: 'Supprimer',
      type: 'warning',
      onConfirm: async () => {
        try {
          const result = await api.scolarite.deletePlanAccompagnementDecision(planId);
          if (result.success) {
            setPlansAccompagnement(prev => prev.filter(plan => plan.id !== planId));
            setFicheEleve(prev => prev ? {
              ...prev,
              plans_accompagnement: (prev.plans_accompagnement || []).filter(plan => plan.id !== planId),
            } : prev);
            showToast('Plan supprime avec succes', 'success');
            return true;
          }
          showToast(result.error || 'Erreur suppression plan', 'error');
          return false;
        } catch (err) {
          showToast('Erreur lors de la suppression du plan', 'error');
          return false;
        }
      },
    });
  }, [showToast]);

  const handleConfirmAction = useCallback(async () => {
    if (!confirmDialog?.onConfirm) return;

    setConfirmLoading(true);
    const shouldClose = await confirmDialog.onConfirm({ decisionCloseComment });
    setConfirmLoading(false);

    if (shouldClose !== false) {
      setConfirmDialog(null);
      setDecisionCloseComment('');
    }
  }, [confirmDialog, decisionCloseComment]);

  const rafraichirVueCourante = useCallback(async (options = {}) => {
    if (vue === 'fiche' && ficheEleve?.eleve?.id) {
      await chargerFicheEleve(ficheEleve.eleve.id, options);
      return;
    }
    await chargerDonnees(options);
  }, [vue, ficheEleve, chargerFicheEleve, chargerDonnees]);

  const verifierChangements = useCallback(async () => {
    if (!selectedClasse || !activeYear || !lastRefreshAt || isCheckingChanges) return;
    setIsCheckingChanges(true);
    try {
      const result = await api.scolarite.checkOutilDecisionnelChanges(selectedClasse, activeYear.id, lastRefreshAt);
      if (result.success && result.data?.changed) {
        await rafraichirVueCourante({ silent: true });
        showToast('Donnees actualisees depuis Vie scolaire et Carnet.', 'info');
      }
    } catch (err) {
      console.warn('Verification changements outil decisionnel impossible:', err);
    } finally {
      setIsCheckingChanges(false);
    }
  }, [selectedClasse, activeYear, lastRefreshAt, isCheckingChanges, rafraichirVueCourante, showToast]);

  const telechargerFicheDecisionnelle = useCallback(async () => {
    if (!ficheEleve?.eleve?.id || !activeYear || pdfDownloading) return;

    setPdfDownloading(true);
    try {
      const result = await api.scolarite.downloadFicheDecisionnellePDF(ficheEleve.eleve.id, activeYear.id);
      if (result.success) {
        showToast('Fiche decisionnelle PDF generee.', 'success');
        return;
      }
      showToast(result.error || 'Erreur generation PDF', 'error');
    } catch (err) {
      showToast('Erreur generation PDF', 'error');
    } finally {
      setPdfDownloading(false);
    }
  }, [ficheEleve, activeYear, pdfDownloading, showToast]);

  useEffect(() => {
    if (!activeYear || !lastRefreshAt || (!selectedClasse && aTraiterScope !== 'global')) return undefined;
    const intervalId = window.setInterval(() => {
      if (aTraiterScope === 'global') {
        chargerDonnees({ silent: true });
      } else {
        verifierChangements();
      }
    }, 45000);
    return () => window.clearInterval(intervalId);
  }, [selectedClasse, activeYear, lastRefreshAt, aTraiterScope, chargerDonnees, verifierChangements]);

  const enregistrerDecisionDirection = useCallback(async () => {
    if (!ficheEleve?.eleve?.id || !activeYear || !decisionForm.decision_finale) return;
    setDecisionSaving(true);
    setError(null);
    try {
      const synthese = ficheEleve.synthese_decisionnelle || {};
      const result = await api.scolarite.createDecisionDirection(ficheEleve.eleve.id, {
        annee_scolaire_id: activeYear.id,
        portee: decisionForm.portee,
        source_type: decisionForm.source_type,
        source_id: decisionForm.source_id || null,
        titre_probleme: decisionForm.titre_probleme?.trim() || null,
        niveau_synthese: synthese.niveau || 'faible',
        score_synthese: synthese.score || 0,
        decision_suggeree: synthese.decision_suggeree || null,
        decision_finale: decisionForm.decision_finale,
        commentaire_direction: decisionForm.commentaire_direction?.trim() || null,
        synthese_snapshot: synthese,
      });

      if (result.success) {
        const createdDecision = result.data;
        setFicheEleve(prev => prev ? {
          ...prev,
          decisions_direction: [createdDecision, ...(prev.decisions_direction || [])],
          decision_direction: createdDecision,
        } : prev);
        setElevesData(prev => prev.map(eleve => (
          eleve.id === ficheEleve.eleve.id
            ? {
              ...eleve,
              decisions_direction: [createdDecision, ...(eleve.decisions_direction || [])],
              decision_direction: createdDecision,
            }
            : eleve
        )));
        setPlanForm(prev => ({
          ...prev,
          decision_direction_id: prev.decision_direction_id || createdDecision?.id || '',
        }));
        setDecisionSourceContext(null);
        setDecisionForm(getDefaultDecisionForm(ficheEleve));
        if (decisionNeedsFollowUp(createdDecision)) {
          ouvrirPlanPourDecision(createdDecision, { silent: true });
        }
        await chargerDonnees({ silent: true });
        showToast(
          decisionNeedsFollowUp(createdDecision)
            ? 'Decision enregistree. Complete maintenant le plan de suivi.'
            : 'Decision direction enregistree.',
          'success'
        );
      } else {
        showToast(result.error || 'Erreur lors de la sauvegarde de la decision', 'error');
      }
    } catch (err) {
      showToast('Erreur lors de la sauvegarde de la decision', 'error');
    } finally {
      setDecisionSaving(false);
    }
  }, [ficheEleve, activeYear, decisionForm, chargerDonnees, getDefaultDecisionForm, ouvrirPlanPourDecision, showToast]);

  const cloturerDecisionDirection = useCallback(async (decision) => {
    if (!decision?.id) return;
    const allPlans = plansAccompagnement.length > 0 ? plansAccompagnement : (ficheEleve?.plans_accompagnement || []);
    const plansActifs = allPlans.filter(plan => (
      Number(plan.decision_direction_id) === Number(decision.id)
      && !['termine', 'annule'].includes(plan.statut)
    ));

    if (plansActifs.length > 0) {
      showToast('Termine ou annule d abord le plan rattache avant de cloturer la decision.', 'error');
      return;
    }

    setDecisionCloseComment(decision.cloture_commentaire || '');
    setConfirmDialog({
      title: 'Cloturer cette decision ?',
      description: 'Indique la solution trouvee avant de cloturer. La decision restera visible dans l historique.',
      confirmText: 'Cloturer',
      type: 'success',
      requireCloseComment: true,
      onConfirm: async ({ decisionCloseComment: closeComment }) => {
        const solution = (closeComment || '').trim();
        if (!solution) {
          showToast('La solution trouvee est obligatoire pour cloturer.', 'error');
          return false;
        }

        setDecisionClosingId(decision.id);
        try {
          const result = await api.scolarite.closeDecisionDirection(decision.id, {
            cloture_commentaire: solution,
          });

          if (result.success) {
            const updatedDecision = result.data;
            setFicheEleve(prev => {
              if (!prev) return prev;
              const decisions = (prev.decisions_direction || []).map(item => (
                item.id === updatedDecision.id ? updatedDecision : item
              ));
              return {
                ...prev,
                decisions_direction: decisions,
                decision_direction: getMainDecisionDirection(decisions),
              };
            });
            setElevesData(prev => prev.map(eleve => {
              if (eleve.id !== ficheEleve?.eleve?.id) return eleve;
              const decisions = (eleve.decisions_direction || []).map(item => (
                item.id === updatedDecision.id ? updatedDecision : item
              ));
              return {
                ...eleve,
                decisions_direction: decisions,
                decision_direction: getMainDecisionDirection(decisions),
              };
            }));
            showToast('Decision cloturee.', 'success');
            setDecisionCloseComment('');
            return true;
          }
          showToast(result.error || 'Erreur lors de la cloture', 'error');
          return false;
        } catch (err) {
          showToast('Erreur lors de la cloture', 'error');
          return false;
        } finally {
          setDecisionClosingId(null);
        }
      },
    });
  }, [ficheEleve, plansAccompagnement, showToast]);

  const handleClasseChange = (classeId, classeNom) => {
    setSelectedClasse(classeId); setSelectedClasseNom(classeNom);
    setClasseSearchTerm(''); setIsClasseDropdownOpen(false);
    setFilterCategorie(''); setFilterTendance(''); setSearchTerm('');
    setLastRefreshAt(null); setToastInfo(null);
    setATraiterScope('classe');
    setATraiter(null); setActiveATraiterTab('all');
    setCurrentPage(1); setVue('dashboard'); setFicheEleve(null);
  };

  const handleATraiterScopeChange = (scope) => {
    if (scope === 'classe' && !selectedClasse) return;
    setATraiterScope(scope);
    setATraiter(null);
    setActiveATraiterTab('all');
    setVue('dashboard');
    setFicheEleve(null);
    setToastInfo(null);
  };

  // ============================================
  // FILTRES, PAGINATION & STATS
  // ============================================

  const elevesFiltres = useMemo(() => {
    return elevesData.filter(e => {
      if (searchTerm && !`${e.nom} ${e.prenom}`.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      if (filterCategorie && getCategorie(e.derniere_moyenne, baremeDe(e)).id !== filterCategorie) return false;
      if (filterTendance && getTendance(e.moyennes, baremeDe(e)).type !== filterTendance) return false;
      return true;
    });
  }, [elevesData, searchTerm, filterCategorie, filterTendance, getCategorie, getTendance]);

  const totalPages = Math.ceil(elevesFiltres.length / ITEMS_PER_PAGE);
  const elevesPagines = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return elevesFiltres.slice(start, start + ITEMS_PER_PAGE);
  }, [elevesFiltres, currentPage]);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, filterCategorie, filterTendance, selectedClasse]);

  const baremeClasse = useMemo(
    () => baremeDe(elevesData.find(e => e?.bareme_max)),
    [elevesData]
  );

  const statsCategories = useMemo(() => {
    const stats = {};
    categoriesConfig.forEach(c => { stats[c.id] = 0; });
    elevesData.forEach(e => { stats[getCategorie(e.derniere_moyenne, baremeDe(e)).id]++; });
    return stats;
  }, [elevesData, categoriesConfig, getCategorie]);

  const statsTendances = useMemo(() => {
    let progression = 0, regression = 0, stable = 0;
    elevesData.forEach(e => {
      const t = getTendance(e.moyennes, baremeDe(e)).type;
      if (t === 'progression') progression++;
      else if (t === 'regression') regression++;
      else if (t === 'stable') stable++;
    });
    return { progression, regression, stable };
  }, [elevesData, getTendance]);

  const statsSynthese = useMemo(() => {
    const stats = {
      faible: 0,
      modere: 0,
      eleve: 0,
      critique: 0,
      avecVieScolaire: 0,
      decisionsValidees: 0,
    };

    elevesData.forEach((eleve) => {
      const niveau = eleve.synthese_decisionnelle?.niveau || 'faible';
      if (stats[niveau] !== undefined) stats[niveau] += 1;
      const vs = eleve.vie_scolaire || {};
      const hasVieScolaire = [
        vs.total_30j,
        vs.ouverts,
        vs.graves,
        vs.critiques,
        vs.absences_30j,
        vs.retards_30j,
        vs.suivis_ouverts,
      ].some((value) => Number(value) > 0);
      if (hasVieScolaire) stats.avecVieScolaire += 1;
      if ((eleve.decisions_direction || []).length > 0 || eleve.decision_direction) stats.decisionsValidees += 1;
    });

    return stats;
  }, [elevesData]);

  const aTraiterSections = useMemo(() => aTraiter?.sections || [], [aTraiter]);
  const aTraiterItems = useMemo(() => (
    aTraiterSections.flatMap(section => (
      (section.items || []).map(item => ({ ...item, sectionKey: section.key, sectionLabel: section.label }))
    ))
  ), [aTraiterSections]);
  const activeATraiterItems = useMemo(() => (
    activeATraiterTab === 'all'
      ? aTraiterItems
      : aTraiterItems.filter(item => item.sectionKey === activeATraiterTab)
  ), [aTraiterItems, activeATraiterTab]);

  // ============================================
  // PAGINATION
  // ============================================

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    return (
      <div className="flex items-center justify-between mt-4 px-2">
        <p className="text-xs text-gray-500">
          {(currentPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, elevesFiltres.length)} sur {elevesFiltres.length} élèves
        </p>
        <div className="flex items-center gap-1">
          <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}
            className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"><ChevronsLeft size={16} className="text-gray-600" /></button>
          <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
            className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"><ChevronLeft size={16} className="text-gray-600" /></button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
            .reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i - 1] > 1) acc.push('...'); acc.push(p); return acc; }, [])
            .map((item, i) => item === '...'
              ? <span key={`d${i}`} className="px-1 text-gray-400 text-xs">...</span>
              : <button key={item} onClick={() => setCurrentPage(item)}
                className={`w-8 h-8 rounded-lg text-xs font-medium transition-colors ${currentPage === item ? 'bg-[#7c3aed] text-white' : 'hover:bg-gray-100 text-gray-600'}`}>{item}</button>
            )}
          <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
            className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"><ChevronRight size={16} className="text-gray-600" /></button>
          <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}
            className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"><ChevronsRight size={16} className="text-gray-600" /></button>
        </div>
      </div>
    );
  };

  // ============================================
  // FILTRES
  // ============================================

  const renderFiltres = () => (
    <div className="bg-white rounded-xl shadow-md p-6 mb-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Classe <span className="text-red-500">*</span></label>
          <div className="relative dropdown-outil-decisionnel">
            <Search className="absolute left-3 top-3.5 h-4 w-4 text-gray-400 pointer-events-none z-10" />
            <input type="text" placeholder="Rechercher une classe..."
              value={selectedClasse ? selectedClasseNom : classeSearchTerm}
              onChange={(e) => { setClasseSearchTerm(e.target.value); setIsClasseDropdownOpen(true); if (selectedClasse) { setSelectedClasse(''); setSelectedClasseNom(''); setElevesData([]); setATraiter(null); setActiveATraiterTab('all'); } }}
              onFocus={() => setIsClasseDropdownOpen(true)}
              className="w-full pl-10 pr-10 py-3 text-base font-medium border-2 border-gray-200 rounded-lg bg-white hover:border-[#7c3aed] focus:border-[#7c3aed] focus:ring-4 focus:ring-[#7c3aed]/10 outline-none transition-all" />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </div>
            {isClasseDropdownOpen && !selectedClasse && (
              <div className="absolute z-20 w-full top-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                {(() => {
                  const term = classeSearchTerm.toLowerCase();
                  const groups = [
                    { label: 'Primaire', items: classesParSection.primaire.filter(c => c.nom.toLowerCase().includes(term)).sort((a, b) => (a.ordre || 0) - (b.ordre || 0)) },
                    { label: 'Collège', items: classesParSection.college.filter(c => c.nom.toLowerCase().includes(term)).sort((a, b) => (a.ordre || 0) - (b.ordre || 0)) },
                    { label: 'Lycée', items: classesParSection.lycee.filter(c => c.nom.toLowerCase().includes(term)).sort((a, b) => (a.ordre || 0) - (b.ordre || 0)) },
                  ];
                  if (!groups.some(g => g.items.length > 0)) return <div className="p-4 text-center text-gray-500 text-sm">Aucune classe trouvée</div>;
                  return groups.map(g => g.items.length === 0 ? null : (
                    <div key={g.label}>
                      <div className="px-4 py-2 bg-gray-100 border-b font-bold text-gray-700 text-sm">{g.label}</div>
                      {g.items.map(classe => (
                        <div key={classe.id} onClick={() => handleClasseChange(classe.id.toString(), classe.nom)}
                          className="px-4 py-3 pl-8 hover:bg-purple-50 cursor-pointer border-b last:border-b-0 transition-colors">
                          <div className="font-medium text-gray-900 text-base">{classe.nom}</div>
                        </div>
                      ))}
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              type="button"
              onClick={() => handleATraiterScopeChange('global')}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-semibold transition-colors ${aTraiterScope === 'global'
                ? 'bg-[#7c3aed] border-[#7c3aed] text-white shadow-sm'
                : 'bg-white border-gray-200 text-gray-600 hover:border-[#7c3aed]/40 hover:bg-purple-50'
                }`}
            >
              <Globe2 size={14} />
              Vue globale direction
            </button>
            {selectedClasse && (
              <button
                type="button"
                onClick={() => handleATraiterScopeChange('classe')}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-semibold transition-colors ${aTraiterScope === 'classe'
                  ? 'bg-gray-900 border-gray-900 text-white shadow-sm'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
              >
                Classe selectionnee
              </button>
            )}
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Rechercher un élève</label>
          <div className="relative">
            <Search className="absolute left-3 top-3.5 h-4 w-4 text-gray-400 pointer-events-none z-10" />
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Nom de l'élève..." disabled={!selectedClasse}
              className="w-full pl-10 pr-4 py-3 text-base font-medium border-2 border-gray-200 rounded-lg bg-white hover:border-[#7c3aed] focus:border-[#7c3aed] focus:ring-4 focus:ring-[#7c3aed]/10 outline-none transition-all disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60" />
          </div>
        </div>
      </div>

      {elevesData.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-5 pt-5 border-t border-gray-100">
          <span className="text-xs text-gray-500 self-center mr-2">Filtrer :</span>
          <button onClick={() => { setFilterCategorie(''); setFilterTendance(''); }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${!filterCategorie && !filterTendance ? 'bg-[#7c3aed] text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            Tous ({elevesData.length})
          </button>
          {categoriesConfig.map(cat => {
            const style = CATEGORIES_STYLE[cat.id] || { color: '#6b7280', bgColor: '#f3f4f6' };
            return (
              <button key={cat.id} onClick={() => { setFilterCategorie(filterCategorie === cat.id ? '' : cat.id); setFilterTendance(''); }}
                className="px-3 py-1.5 rounded-full text-xs font-medium transition-all border"
                style={{ backgroundColor: filterCategorie === cat.id ? style.color : style.bgColor, color: filterCategorie === cat.id ? 'white' : style.color, borderColor: style.color }}>
                {cat.label} ({statsCategories[cat.id] || 0})
              </button>
            );
          })}
          <div className="w-px bg-gray-300 mx-1 self-stretch" />
          {[
            { id: 'progression', label: 'Progression', count: statsTendances.progression },
            { id: 'regression', label: 'Régression', count: statsTendances.regression },
            { id: 'stable', label: 'Stable', count: statsTendances.stable },
          ].map(t => (
            <button key={t.id} onClick={() => { setFilterTendance(filterTendance === t.id ? '' : t.id); setFilterCategorie(''); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${filterTendance === t.id ? 'bg-[#7c3aed] text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {t.label} ({t.count})
            </button>
          ))}
        </div>
      )}

      {selectedClasse && elevesData.length > 0 && (
        <div className="mt-5 pt-5 border-t border-gray-200">
          <div className="flex gap-8 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#7c3aed]"></div>
              <span className="text-gray-800">Élèves :</span>
              <span className="font-bold text-[#7c3aed]">{elevesData.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#059669]"></div>
              <span className="text-gray-800">Moy. classe :</span>
              <span className="font-bold text-[#059669]">
                {(elevesData.reduce((s, e) => s + (parseFloat(e.derniere_moyenne) || 0), 0) / elevesData.length).toFixed(2)}/{baremeClasse}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderDashboardKpiCard = ({
    id,
    label,
    value,
    helper,
    icon: IconComp,
    color,
    active,
    onClick,
  }) => (
    <button
      key={id}
      type="button"
      onClick={onClick}
      className="group relative overflow-hidden rounded-lg border bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
      style={{
        borderColor: active ? color : '#e5e7eb',
        boxShadow: active ? `0 10px 24px ${color}24` : undefined,
      }}
    >
      <div
        className="absolute inset-x-0 top-0 h-1"
        style={{ backgroundColor: color, opacity: active ? 1 : 0.35 }}
      />
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
          <p className="mt-2 text-3xl font-bold leading-none text-slate-900">{value}</p>
        </div>
        <div
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg transition-transform group-hover:scale-105"
          style={{ backgroundColor: `${color}14`, color }}
        >
          <IconComp size={19} />
        </div>
      </div>
      <p className="mt-3 text-xs font-medium text-slate-400">{helper}</p>
    </button>
  );

  const renderATraiter = () => {
    const isGlobalATraiter = aTraiterScope === 'global';
    if (!activeYear || (!isGlobalATraiter && !selectedClasse)) return null;
    if (loading && selectedClasse) return null;

    const total = aTraiter?.total || 0;
    const urgent = aTraiter?.urgent || 0;
    const hasItems = activeATraiterItems.length > 0;
    const scopeLabel = isGlobalATraiter
      ? 'Vue globale direction - toutes les classes'
      : selectedClasseNom || 'Classe selectionnee';

    return (
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 shadow-sm">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-50 border border-red-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={19} className="text-red-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                A traiter
                {total > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-50 text-red-700">
                    {total}
                  </span>
                )}
              </h3>
              <p className="text-xs text-gray-400 mt-1">
                {scopeLabel} - {urgent > 0 ? `${urgent} point${urgent > 1 ? 's' : ''} urgent${urgent > 1 ? 's' : ''}` : 'Aucun point urgent'}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <div className="inline-flex items-center rounded-lg border border-gray-200 bg-gray-50 p-1">
              <button
                type="button"
                onClick={() => handleATraiterScopeChange('classe')}
                disabled={!selectedClasse}
                className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${aTraiterScope === 'classe' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'
                  }`}
              >
                Classe
              </button>
              <button
                type="button"
                onClick={() => handleATraiterScopeChange('global')}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${aTraiterScope === 'global' ? 'bg-white text-[#7c3aed] shadow-sm' : 'text-gray-500 hover:text-gray-800'
                  }`}
              >
                <Globe2 size={12} />
                Global
              </button>
            </div>

          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <button
            type="button"
            onClick={() => setActiveATraiterTab('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${activeATraiterTab === 'all'
              ? 'bg-[#7c3aed] border-[#7c3aed] text-white'
              : 'bg-white border-gray-200 text-gray-500 hover:border-[#7c3aed]/40'
              }`}
          >
            Tous ({total})
          </button>
          {aTraiterSections.map((section) => {
            const style = A_TRAITER_STYLES[section.key] || A_TRAITER_STYLES.plan_echeance;
            const count = section.items?.length || 0;
            const IconComp = style.icon;

            return (
              <button
                key={section.key}
                type="button"
                onClick={() => setActiveATraiterTab(section.key)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors"
                style={{
                  backgroundColor: activeATraiterTab === section.key ? style.color : style.bg,
                  borderColor: activeATraiterTab === section.key ? style.color : style.border,
                  color: activeATraiterTab === section.key ? '#ffffff' : style.color,
                }}
              >
                <IconComp size={13} />
                {section.label} ({count})
              </button>
            );
          })}
        </div>

        {aTraiterLoading ? (
          <div className="flex items-center justify-center py-8 text-gray-400">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#7c3aed] mr-2"></div>
            <span className="text-sm">Chargement des points a traiter...</span>
          </div>
        ) : hasItems ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {activeATraiterItems.slice(0, 8).map((item) => {
              const style = A_TRAITER_STYLES[item.sectionKey] || A_TRAITER_STYLES.plan_echeance;
              const IconComp = style.icon;
              const severityLabel = SEVERITY_LABELS[item.severity] || 'Info';

              return (
                <div key={`${item.sectionKey}-${item.id}`} className="rounded-lg border p-3" style={{ borderColor: style.border, backgroundColor: style.bg }}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white border" style={{ color: style.color, borderColor: style.border }}>
                          <IconComp size={11} /> {item.sectionLabel}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${item.severity === 'critical'
                          ? 'bg-red-100 text-red-700'
                          : item.severity === 'warning'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-sky-100 text-sky-700'
                          }`}>
                          {severityLabel}
                        </span>
                        {item.badge && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white text-gray-500 border border-gray-200">
                            {item.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-gray-800">{item.eleve_nom}</p>
                      <p className="text-sm text-gray-700 mt-1">{item.title}</p>
                      {item.description && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2 whitespace-pre-wrap">{item.description}</p>
                      )}
                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-400 mt-2">
                        {item.classe_nom && <span>{item.classe_nom}</span>}
                        {item.date && <span>{formatDateTime(item.date)}</span>}
                        {item.meta && <span>{item.meta}</span>}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => ouvrirFicheDepuisATraiter(item)}
                      className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-xs font-semibold text-gray-600 hover:border-[#7c3aed]/40 hover:bg-purple-50 transition-colors flex-shrink-0"
                    >
                      {item.type === 'vie_scolaire_urgente' ? 'Traiter' : 'Details'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <CheckCircle size={32} className="mx-auto mb-2 text-emerald-300" />
            <p className="text-sm text-gray-400">Aucun element dans cette categorie.</p>
          </div>
        )}

        {activeATraiterItems.length > 8 && (
          <p className="text-xs text-gray-400 mt-3 text-right">
            {activeATraiterItems.length - 8} autre{activeATraiterItems.length - 8 > 1 ? 's' : ''} element{activeATraiterItems.length - 8 > 1 ? 's' : ''} a consulter via les fiches eleves.
          </p>
        )}
      </div>
    );
  };

  // ============================================
  // DASHBOARD
  // ============================================

  const renderDashboard = () => (
    <div ref={pageTopRef}>
      {elevesData.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Target size={16} className="text-[#7c3aed]" />
              <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Seuils actifs</span>
            </div>
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Activity size={12} /> Stabilité : ±{noteEnFrancais(seuilSurBareme(seuilStabilite, baremeClasse))} pts
            </span>
          </div>
          {/* Barre visuelle des seuils */}
          <div className="flex rounded-lg overflow-hidden h-7">
            {categoriesConfig.slice().sort((a, b) => a.min - b.min).map((cat) => {
              const style = CATEGORIES_STYLE[cat.id] || { color: '#6b7280', bgColor: '#f3f4f6' };
              const width = ((cat.max - cat.min) / 20) * 100;
              return (
                <div key={cat.id} className="flex items-center justify-center text-white text-[10px] font-semibold relative group/seg cursor-default"
                  style={{ width: `${width}%`, backgroundColor: style.color, opacity: 0.85 }}>
                  <span className="truncate px-1">{cat.label}</span>
                  <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-white text-gray-800 text-xs rounded-lg shadow-xl p-2.5 hidden group-hover/seg:block z-50 border whitespace-nowrap"
                    style={{ borderColor: style.color }}>
                    <p className="font-semibold" style={{ color: style.color }}>{cat.label}</p>
                    <p className="text-gray-500 mt-0.5">Moyenne entre <span className="font-bold">{noteEnFrancais(seuilSurBareme(cat.min, baremeClasse))}</span> et <span className="font-bold">{noteEnFrancais(seuilSurBareme(cat.max, baremeClasse))}</span>/{baremeClasse}</p>
                    <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white transform rotate-45" style={{ borderRight: `1px solid ${style.color}`, borderBottom: `1px solid ${style.color}` }}></div>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Graduations avec seuils visibles */}
          <div className="relative h-5 mt-1">
            {/* 0 à gauche */}
            <span className="absolute left-0 text-[10px] text-gray-400 -translate-x-0">0</span>
            {/* Seuils intermédiaires */}
            {categoriesConfig.slice().sort((a, b) => a.min - b.min).map((cat) => {
              if (cat.min <= 0) return null;
              const leftPct = (cat.min / 20) * 100;
              const style = CATEGORIES_STYLE[cat.id] || { color: '#6b7280' };
              return (
                <span key={`tick-${cat.id}`} className="absolute text-[10px] font-semibold -translate-x-1/2"
                  style={{ left: `${leftPct}%`, color: style.color }}>
                  {noteEnFrancais(seuilSurBareme(cat.min, baremeClasse))}
                </span>
              );
            })}
            {/* Le barème à droite (20, ou 10 au primaire) */}
            <span className="absolute right-0 text-[10px] text-gray-400 translate-x-0">{baremeClasse}</span>
          </div>
        </div>
      )}

      {elevesData.length > 0 && (
        <div className="mb-6 space-y-5">
          <section>
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-800">Répartition par niveau</h3>
                <p className="text-xs text-slate-400">Classement selon la dernière moyenne calculée</p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {categoriesConfig.map(cat => {
                const count = statsCategories[cat.id] || 0;
                const pct = ((count / elevesData.length) * 100).toFixed(0);
                const style = CATEGORIES_STYLE[cat.id] || { color: '#6b7280', icon: Target };
                return renderDashboardKpiCard({
                  id: cat.id,
                  label: cat.label,
                  value: count,
                  helper: `${pct}% de la classe`,
                  icon: style.icon,
                  color: style.color,
                  active: filterCategorie === cat.id,
                  onClick: () => {
                    setFilterCategorie(filterCategorie === cat.id ? '' : cat.id);
                    setFilterTendance('');
                    scrollToResults();
                  },
                });
              })}
            </div>
          </section>

          <section>
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-800">Tendances</h3>
                <p className="text-xs text-slate-400">Évolution entre les dernières périodes disponibles</p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {[
                {
                  id: 'progression',
                  label: 'En progression',
                  value: statsTendances.progression,
                  helper: 'Moyenne en hausse',
                  icon: TrendingUp,
                  color: '#059669',
                },
                {
                  id: 'stable',
                  label: 'Stables',
                  value: statsTendances.stable,
                  helper: `Variation dans ±${seuilStabilite} pts`,
                  icon: Minus,
                  color: '#d97706',
                },
                {
                  id: 'regression',
                  label: 'En régression',
                  value: statsTendances.regression,
                  helper: 'Moyenne en baisse',
                  icon: TrendingDown,
                  color: '#dc2626',
                },
              ].map(item => renderDashboardKpiCard({
                ...item,
                active: filterTendance === item.id,
                onClick: () => {
                  setFilterTendance(filterTendance === item.id ? '' : item.id);
                  setFilterCategorie('');
                  scrollToResults();
                },
              }))}
            </div>
          </section>
        </div>
      )}

      {elevesData.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <ClipboardCheck size={17} className="text-[#7c3aed]" />
              <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Synthese decisionnelle</span>
            </div>

          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {[
              { key: 'critique', label: 'Critiques', value: statsSynthese.critique },
              { key: 'eleve', label: 'Eleves', value: statsSynthese.eleve },
              { key: 'modere', label: 'Moderes', value: statsSynthese.modere },
              { key: 'faible', label: 'Faibles', value: statsSynthese.faible },
            ].map((item) => {
              const style = getSyntheseStyle(item.key);
              const IconComp = style.icon;
              return (
                <div key={item.key} className="rounded-lg border p-3" style={{ borderColor: style.color, backgroundColor: style.bgColor }}>
                  <div className="flex items-center gap-2 mb-1">
                    <IconComp size={15} style={{ color: style.color }} />
                    <span className="text-[11px] font-semibold uppercase" style={{ color: style.color }}>{item.label}</span>
                  </div>
                  <div className="text-2xl font-bold" style={{ color: style.color }}>{item.value}</div>
                </div>
              );
            })}
            <div className="rounded-lg border border-sky-200 bg-sky-50 p-3">
              <div className="text-[11px] font-semibold uppercase text-sky-700 mb-1">Avec Vie scolaire</div>
              <div className="text-2xl font-bold text-sky-700">{statsSynthese.avecVieScolaire}</div>
            </div>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
              <div className="text-[11px] font-semibold uppercase text-emerald-700 mb-1">Validees</div>
              <div className="text-2xl font-bold text-emerald-700">{statsSynthese.decisionsValidees}</div>
            </div>
          </div>
        </div>
      )}

      {renderATraiter()}

      <div ref={resultsRef} className="scroll-mt-6" />

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7c3aed]"></div>
        </div>
      ) : !selectedClasse ? (
        <div className="text-center py-20 text-gray-400">
          <BrainCircuit size={48} className="mx-auto mb-4 opacity-30" />
          <p className="text-lg font-medium">Sélectionnez une classe</p>
          <p className="text-sm mt-1">
            {aTraiterScope === 'global'
              ? 'pour ouvrir le tableau pédagogique détaillé'
              : 'pour afficher le tableau de bord décisionnel'}
          </p>
        </div>
      ) : elevesFiltres.length === 0 && elevesData.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Users size={40} className="mx-auto mb-3 opacity-30" />
          <p>Aucune donnée disponible pour cette classe</p>
          <p className="text-xs mt-1">Les moyennes doivent être calculées pour alimenter cet outil</p>
        </div>
      ) : elevesFiltres.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Users size={40} className="mx-auto mb-3 opacity-30" />
          <p>Aucun élève trouvé avec ces critères</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-[#7c3aed] to-[#6d28d9] text-white">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider">Élève</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold uppercase tracking-wider">Catégorie</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold uppercase tracking-wider">Dernière moy.</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold uppercase tracking-wider">Tendance</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold uppercase tracking-wider">Évolution</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold uppercase tracking-wider">Alertes</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold uppercase tracking-wider">Vie scolaire</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold uppercase tracking-wider">Synthese</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold uppercase tracking-wider">Decision</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {elevesPagines.map((eleve, idx) => {
                    const baremeEleve = baremeDe(eleve);
                    const moitieBareme = seuilSurBareme(10, baremeEleve);
                    const categorie = getCategorie(eleve.derniere_moyenne, baremeEleve);
                    const tendance = getTendance(eleve.moyennes, baremeEleve);
                    const TendIcon = tendance.icon;
                    const periodesEnDifficulte = (eleve.moyennes || []).filter(m => m < moitieBareme).length;
                    const vieScolaire = eleve.vie_scolaire || {};
                    const synthese = eleve.synthese_decisionnelle || {};
                    const syntheseStyle = getSyntheseStyle(synthese.niveau);
                    const SyntheseIcon = syntheseStyle.icon;

                    return (
                      <tr key={eleve.id || idx} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-sm text-gray-800">{eleve.nom} {eleve.prenom}</div>
                          <div className="text-xs text-gray-400">{eleve.matricule || ''}</div>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold"
                            style={{ backgroundColor: categorie.bgColor, color: categorie.color }}>
                            {React.createElement(categorie.icon, { size: 12 })} {categorie.label}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className="text-lg font-bold" style={{ color: categorie.color }}>{formatMoyenne(eleve.derniere_moyenne)}</span>
                          <span className="text-xs text-gray-400">/{baremeEleve}</span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          {TendIcon ? (
                            <div className="flex items-center justify-center gap-1">
                              <TendIcon size={16} style={{ color: tendance.color }} />
                              <span className="text-xs font-semibold" style={{ color: tendance.color }}>{tendance.diff}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">{tendance.label}</span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-end gap-1 justify-center h-8">
                            {(eleve.moyennes || []).slice(-6).map((m, i) => {
                              const height = Math.max(4, (m / baremeEleve) * 100);
                              const catM = getCategorie(m, baremeEleve);
                              return <div key={i} className="w-3 rounded-t transition-all"
                                style={{ height: `${height}%`, backgroundColor: catM.color, opacity: 0.7 + (i * 0.05) }}
                                title={`${formatMoyenne(m)}/${baremeEleve}`} />;
                            })}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center">
                          {periodesEnDifficulte > 0 ? (
                            <div className="relative group inline-flex">
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-600 cursor-help">
                                <AlertTriangle size={12} /> {periodesEnDifficulte}
                              </span>
                              <div className="absolute right-0 bottom-full mb-2 w-64 bg-white text-gray-800 text-xs rounded-lg shadow-xl p-3 hidden group-hover:block z-50 border border-red-200">
                                <div className="flex items-start gap-2">
                                  <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <p className="font-semibold text-red-600 mb-1">Attention requise</p>
                                    <p className="text-gray-600">
                                      {eleve.prenom} a eu une moyenne <span className="font-semibold">{'<'} {noteEnFrancais(moitieBareme)}/{baremeEleve}</span> pendant <span className="font-bold text-red-600">{periodesEnDifficulte} période{periodesEnDifficulte > 1 ? 's' : ''}</span>.
                                    </p>
                                    {periodesEnDifficulte >= 2 && (
                                      <p className="text-red-500 mt-2 text-xs bg-red-50 p-2 rounded font-medium">
                                        ⚠️ Convocation parents et plan de soutien recommandés.
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-r border-b border-red-200 transform rotate-45"></div>
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-green-600 font-medium">✓ Aucune</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold ${Number(vieScolaire.graves || 0) > 0 ? 'bg-red-50 text-red-600' : Number(vieScolaire.ouverts || 0) > 0 ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-500'}`}>
                              <BookOpen size={11} /> {vieScolaire.total_30j || 0} evt.
                            </span>
                            {(Number(vieScolaire.ouverts || 0) > 0 || Number(vieScolaire.suivis_ouverts || 0) > 0) && (
                              <span className="text-[10px] text-gray-400">
                                {vieScolaire.ouverts || 0} ouvert(s) / {vieScolaire.suivis_ouverts || 0} suivi(s)
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold"
                              style={{ backgroundColor: syntheseStyle.bgColor, color: syntheseStyle.color }}>
                              <SyntheseIcon size={11} /> {synthese.score ?? 0}/100
                            </span>
                            <span className="text-[10px] text-gray-500">{synthese.decision_suggeree_label || '-'}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center">
                          {eleve.decision_direction ? (
                            <div className="flex flex-col items-center gap-1">
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700">
                                <CheckCircle size={11} /> Validee
                              </span>
                              <span className="text-[10px] text-gray-500">{eleve.decision_direction.decision_finale_label}</span>
                            </div>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-500">
                              <Clock size={11} /> A valider
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <button
                            onClick={() => chargerFicheEleve(eleve.id)}
                            disabled={Boolean(ficheLoadingEleveId)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#7c3aed]/10 text-[#7c3aed] rounded-lg text-xs font-medium hover:bg-[#7c3aed]/20 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {Number(ficheLoadingEleveId) === Number(eleve.id) ? (
                              <span className="h-3.5 w-3.5 rounded-full border-2 border-[#7c3aed]/30 border-b-[#7c3aed] animate-spin" />
                            ) : (
                              <Eye size={14} />
                            )}
                            {Number(ficheLoadingEleveId) === Number(eleve.id) ? 'Ouverture...' : 'Détails'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          {renderPagination()}
        </>
      )}
    </div>
  );

  // ============================================
  // FICHE ÉLÈVE
  // ============================================

  const renderFicheEleve = () => {
    if (!ficheEleve) return null;
    const { eleve, historique, matieres_fortes, matieres_faibles } = ficheEleve;
    const baremeFiche = baremeDe(eleve);
    const categorie = getCategorie(eleve?.derniere_moyenne, baremeFiche);
    const vieScolaire = ficheEleve.vie_scolaire || {};
    const vieScolaireStats = vieScolaire.stats || {};
    const evenementsVieScolaire = vieScolaire.evenements_recents || [];
    const synthese = ficheEleve.synthese_decisionnelle || {};
    const syntheseStyle = getSyntheseStyle(synthese.niveau);
    const SyntheseIcon = syntheseStyle.icon;
    const decisionsDirection = ficheEleve.decisions_direction || (ficheEleve.decision_direction ? [ficheEleve.decision_direction] : []);
    const decisionDirection = getMainDecisionDirection(decisionsDirection);
    const plans = plansAccompagnement.length > 0 ? plansAccompagnement : (ficheEleve.plans_accompagnement || []);
    const plansActifsCount = plans.filter(plan => !['termine', 'annule'].includes(plan.statut)).length;
    const getDecisionFollowUpState = (decision) => {
      const relatedPlans = plans.filter(plan => Number(plan.decision_direction_id) === Number(decision.id));
      const activePlans = relatedPlans.filter(plan => !['termine', 'annule'].includes(plan.statut));
      const completedPlans = relatedPlans.filter(plan => plan.statut === 'termine');
      const relatedActions = journalActions.filter(action => Number(action.decision_direction_id) === Number(decision.id));
      const relatedCommunications = communicationsParents.filter(communication => Number(communication.decision_direction_id) === Number(decision.id));
      const hasFollowUp = relatedPlans.some(plan => plan.statut !== 'annule') || relatedActions.length > 0 || relatedCommunications.length > 0;

      return { relatedPlans, activePlans, completedPlans, relatedActions, relatedCommunications, hasFollowUp };
    };
    const decisionsSansSuivi = decisionsDirection.filter((decision) => (
      decisionNeedsFollowUp(decision) && !getDecisionFollowUpState(decision).hasFollowUp
    ));
    const timelineItems = [
      ...journalActions.map((action) => ({
        kind: 'action',
        id: `action-${action.id}`,
        sortDate: action.created_at,
        action,
      })),
      ...communicationsParents.map((communication) => ({
        kind: 'communication',
        id: `communication-${communication.id}`,
        sortDate: communication.sent_at || communication.created_at,
        communication,
      })),
    ].sort((a, b) => new Date(b.sortDate || 0) - new Date(a.sortDate || 0));

    return (
      <div>
        <div className="flex items-center justify-between gap-3 mb-4">
          <button onClick={() => { setVue('dashboard'); setFicheEleve(null); }}
            className="flex items-center gap-2 text-sm text-[#7c3aed] hover:text-[#6d28d9] font-medium">
            <ChevronLeft size={18} /> Retour au tableau de bord
          </button>
          <button
            type="button"
            onClick={telechargerFicheDecisionnelle}
            disabled={pdfDownloading}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#7c3aed] text-white text-xs font-semibold hover:bg-[#6d28d9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          >
            {pdfDownloading ? (
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
            ) : (
              <Download size={14} />
            )}
            PDF synthese
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">{eleve?.nom} {eleve?.prenom}</h2>
              <p className="text-sm text-gray-500 mt-1">{eleve?.classe_nom} • {eleve?.matricule || 'N/A'}</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold" style={{ color: categorie.color }}>
                {formatMoyenne(eleve?.derniere_moyenne)}<span className="text-base text-gray-400">/{baremeFiche}</span>
              </div>
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold mt-2"
                style={{ backgroundColor: categorie.bgColor, color: categorie.color }}>
                {React.createElement(categorie.icon, { size: 12 })} {categorie.label}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-md p-5 lg:col-span-2">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                  <ClipboardCheck size={18} className="text-[#7c3aed]" /> Synthese decisionnelle
                </h3>
                <p className="text-xs text-gray-400 mt-1">
                  Croisement Carnet d'evaluation + Vie scolaire, recalcule a chaque actualisation.
                </p>
              </div>
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold"
                style={{ backgroundColor: syntheseStyle.bgColor, color: syntheseStyle.color }}>
                <SyntheseIcon size={13} /> {synthese.niveau_label || syntheseStyle.label} - {synthese.score ?? 0}/100
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                <p className="text-[11px] uppercase font-semibold text-slate-500 mb-1">Decision suggeree</p>
                <p className="text-sm font-semibold text-slate-800">{synthese.decision_suggeree_label || '-'}</p>
              </div>
              <div className="rounded-lg bg-sky-50 border border-sky-200 p-3">
                <p className="text-[11px] uppercase font-semibold text-sky-600 mb-1">Origine donnees</p>
                <div className="flex flex-wrap gap-1">
                  {(synthese.origine_donnees || []).map((source) => (
                    <span key={source} className="px-2 py-0.5 rounded-full bg-white text-sky-700 text-[10px] font-semibold border border-sky-100">
                      {source === 'vie_scolaire' ? 'Vie scolaire' : 'Carnet'}
                    </span>
                  ))}
                </div>
              </div>
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                <p className="text-[11px] uppercase font-semibold text-emerald-700 mb-1">Decision direction</p>
                <p className="text-sm font-semibold text-emerald-800">
                  {decisionDirection ? decisionDirection.decision_finale_label : 'A valider'}
                </p>
              </div>
            </div>

            {synthese.motifs?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {synthese.motifs.map((motif, index) => (
                  <span key={`${motif}-${index}`} className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
                    {motif}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-md p-5">
            <h3 className="font-semibold text-gray-700 flex items-center gap-2 mb-4">
              <BookOpen size={18} className="text-[#7c3aed]" /> Vie scolaire
            </h3>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {[
                { label: '30j', value: vieScolaireStats.total_30j || 0 },
                { label: 'Ouverts', value: vieScolaireStats.ouverts || 0 },
                { label: 'Graves', value: vieScolaireStats.graves || 0 },
                { label: 'Suivis', value: vieScolaireStats.suivis_ouverts || 0 },
              ].map((item) => (
                <div key={item.label} className="rounded-lg border border-slate-200 bg-slate-50 p-2 text-center">
                  <div className="text-lg font-bold text-slate-800">{item.value}</div>
                  <div className="text-[10px] uppercase font-semibold text-slate-400">{item.label}</div>
                </div>
              ))}
            </div>
            {evenementsVieScolaire.length > 0 ? (
              <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {evenementsVieScolaire.slice(0, 5).map((event) => (
                  <div key={event.id} className="rounded-lg border border-slate-200 p-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-slate-700 truncate">{event.titre || event.type}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${event.gravite === 'critique' || event.gravite === 'grave' ? 'bg-red-50 text-red-600' : 'bg-slate-100 text-slate-500'}`}>
                        {event.gravite}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-1">
                      <p className="text-[10px] text-slate-400">{formatDateTime(event.date_evenement)}</p>
                      {(event.gravite === 'critique' || event.gravite === 'grave') && (
                        <button
                          type="button"
                          onClick={() => preparerDecisionDepuisEvenement(event)}
                          className="px-2 py-1 rounded-md text-[10px] font-semibold text-[#7c3aed] hover:bg-purple-50 transition-colors"
                        >
                          Decider
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-5">Aucun evenement Vie scolaire rattache.</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-md p-5">
            <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Activity size={18} className="text-[#7c3aed]" />
              Évolution des moyennes
            </h3>
            {historique && historique.length > 0 ? (
              <div className="space-y-3">
                {historique.map((h, i) => {
                  const cat = getCategorie(h.moyenne, baremeFiche);
                  const prev = i > 0 ? historique[i - 1].moyenne : null;
                  const diff = prev !== null ? (h.moyenne - prev).toFixed(2) : null;
                  return (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                      <div className="text-xs text-gray-500 w-24 font-medium">{h.periode}</div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div className="h-2 rounded-full transition-all" style={{ width: `${(h.moyenne / baremeFiche) * 100}%`, backgroundColor: cat.color, minWidth: '8px' }} />
                          <span className="text-sm font-bold" style={{ color: cat.color }}>{formatMoyenne(h.moyenne)}</span>
                        </div>
                      </div>
                      {diff !== null && (
                        <span className={`text-xs font-semibold ${parseFloat(diff) > 0 ? 'text-green-600' : parseFloat(diff) < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                          {parseFloat(diff) > 0 ? '+' : ''}{diff}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : <p className="text-sm text-gray-400 text-center py-8">Pas encore d'historique disponible</p>}
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-md p-5">
              <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <ArrowUpRight size={18} className="text-green-600" /> Points forts
              </h3>
              {matieres_fortes && matieres_fortes.length > 0 ? (
                <div className="space-y-2">
                  {matieres_fortes.map((m, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-green-50">
                      <span className="text-sm font-medium text-gray-700">{m.matiere}</span>
                      <span className="text-sm font-bold text-green-600">{formatMoyenne(m.moyenne)}/{baremeFiche}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-gray-400">Données insuffisantes</p>}
            </div>

            <div className="bg-white rounded-xl shadow-md p-5">
              <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <ArrowDownRight size={18} className="text-red-600" /> Points faibles
              </h3>
              {matieres_faibles && matieres_faibles.length > 0 ? (
                <div className="space-y-2">
                  {matieres_faibles.map((m, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-red-50">
                      <span className="text-sm font-medium text-gray-700">{m.matiere}</span>
                      <span className="text-sm font-bold text-red-600">{formatMoyenne(m.moyenne)}/{baremeFiche}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-gray-400">Données insuffisantes</p>}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-5 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-700 flex items-center gap-2">
              <BookOpen size={18} className="text-[#7c3aed]" /> Suivi des decisions
              {timelineItems.length > 0 && (
                <span className="text-xs font-normal text-gray-400">({timelineItems.length} entree{timelineItems.length > 1 ? 's' : ''})</span>
              )}
            </h3>
            {!showJournalForm && (
              <button onClick={() => setShowJournalForm(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#7c3aed] text-white rounded-lg text-xs font-medium hover:bg-[#6d28d9] transition-colors">
                <Plus size={14} /> Ajouter un suivi
              </button>
            )}
          </div>

          {/* Formulaire d'ajout */}
          {showJournalForm && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Plus size={16} className="text-[#7c3aed]" />
                <span className="text-sm font-semibold text-[#7c3aed]">Nouvelle entrée</span>
              </div>
              {decisionsDirection.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-semibold text-gray-600 mb-2">Rattacher a une decision</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setNewAction(prev => ({ ...prev, decision_direction_id: '' }))}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${!newAction.decision_direction_id
                        ? 'bg-[#7c3aed] border-[#7c3aed] text-white'
                        : 'bg-white border-gray-200 text-gray-500 hover:border-[#7c3aed]/40'
                        }`}
                    >
                      General
                    </button>
                    {decisionsDirection.map((decision) => (
                      <button
                        key={decision.id}
                        type="button"
                        onClick={() => setNewAction(prev => ({ ...prev, decision_direction_id: decision.id }))}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${Number(newAction.decision_direction_id) === Number(decision.id)
                          ? 'bg-[#7c3aed] border-[#7c3aed] text-white'
                          : 'bg-white border-gray-200 text-gray-500 hover:border-[#7c3aed]/40'
                          }`}
                      >
                        {decision.titre_probleme || decision.decision_finale_label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3 mb-3">
                {ACTION_TYPE_OPTIONS.map(t => (
                  <button key={t.value} onClick={() => setNewAction(prev => ({ ...prev, type: t.value }))}
                    className="px-3 py-2 rounded-lg text-xs font-semibold border-2 transition-all text-center"
                    style={{
                      backgroundColor: newAction.type === t.value ? t.color : t.bg,
                      color: newAction.type === t.value ? 'white' : t.color,
                      borderColor: t.color,
                      transform: newAction.type === t.value ? 'scale(1.02)' : 'scale(1)',
                    }}>
                    {t.label}
                  </button>
                ))}
              </div>
              {newAction.type === 'contact_parent' ? (
                <div className="rounded-xl border border-sky-100 bg-white p-3">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="text-xs font-semibold text-gray-700 flex items-center gap-2">
                        <Phone size={14} className="text-sky-600" /> Contact parent
                      </p>
                      <p className="text-[11px] text-gray-400 mt-1">
                        Le contact est enregistre dans le suivi. Quand OVH sera configure, le SMS pourra partir directement.
                      </p>
                    </div>
                    {!smsConfig?.configured && (
                      <span className="px-2 py-1 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700">
                        SMS plus tard
                      </span>
                    )}
                  </div>

                  {parentsEleve.length > 0 ? (
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-gray-600 mb-2">Parent a contacter</p>
                      <div className="flex flex-wrap gap-2">
                        {parentsEleve.map(parent => (
                          <button
                            key={parent.id}
                            type="button"
                            onClick={() => setNewAction(prev => ({ ...prev, parent_id: parent.id }))}
                            className={`px-3 py-2 rounded-lg border text-xs font-semibold text-left transition-colors ${Number(newAction.parent_id) === Number(parent.id)
                              ? 'bg-sky-600 border-sky-600 text-white'
                              : 'bg-white border-gray-200 text-gray-600 hover:border-sky-300'
                              }`}
                          >
                            <span className="block">{parent.nom_prenom}</span>
                            <span className="block text-[10px] opacity-75">{parent.telephone_normalise || 'Telephone absent'}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 mb-3">Aucun parent lie a cet eleve pour le moment.</p>
                  )}

                  <textarea
                    value={newAction.contact_message}
                    onChange={e => setNewAction(prev => ({ ...prev, contact_message: e.target.value }))}
                    placeholder={buildSmsMessage(newAction.decision_direction_id)}
                    rows={3}
                    className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 outline-none transition-all resize-none"
                  />
                  <p className="text-[11px] text-gray-400 mt-2">
                    {(newAction.contact_message || buildSmsMessage(newAction.decision_direction_id)).length} caractere(s)
                  </p>
                </div>
              ) : (
                <textarea
                  value={newAction.description}
                  onChange={e => setNewAction(prev => ({ ...prev, description: e.target.value }))}
                  placeholder={
                    newAction.type === 'convocation' ? 'Motif de la convocation des parents...' :
                      newAction.type === 'recommandation' ? 'Soutien scolaire, tutorat, changement de place...' :
                        newAction.type === 'sanction' ? 'Nature de la sanction et motif...' :
                          'Note ou observation sur l\'élève...'
                  }
                  rows={3}
                  className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-[#7c3aed] focus:ring-4 focus:ring-[#7c3aed]/10 outline-none transition-all resize-none"
                />
              )}
              <div className="flex items-center justify-end gap-2 mt-3">
                <button onClick={() => { setShowJournalForm(false); resetActionForm(); }}
                  className="px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                  Annuler
                </button>
                <button onClick={ajouterAction}
                  disabled={journalSubmitting || smsSending || (
                    newAction.type === 'contact_parent'
                      ? !newAction.parent_id
                      : !newAction.description.trim()
                  )}
                  className="flex items-center gap-1.5 px-4 py-2 bg-[#7c3aed] text-white rounded-lg text-xs font-medium hover:bg-[#6d28d9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {journalSubmitting || smsSending ? (
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                  ) : (
                    <Send size={13} />
                  )}
                  {newAction.type === 'contact_parent'
                    ? (smsConfig?.configured ? 'Envoyer et enregistrer' : 'Enregistrer le contact prevu')
                    : 'Enregistrer'}
                </button>
              </div>
            </div>
          )}

          {/* Liste des actions */}
          {timelineItems.length > 0 ? (
            <div className="space-y-3">
              {timelineItems.map((item) => {
                if (item.kind === 'communication') {
                  const communication = item.communication;
                  const statusStyle = SMS_STATUS_STYLE[communication.statut] || SMS_STATUS_STYLE.en_attente;
                  const dateFormatted = communication.sent_at || communication.created_at
                    ? new Date(communication.sent_at || communication.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                    : '';

                  return (
                    <div key={item.id} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border-l-4 group hover:bg-gray-100 transition-colors"
                      style={{ borderLeftColor: '#0284c7' }}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-50 text-sky-700">
                            Contact parent
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusStyle.className}`}>
                            SMS {statusStyle.label}
                          </span>
                          <span className="text-[10px] text-gray-400 flex items-center gap-1">
                            <Calendar size={10} /> {dateFormatted}
                          </span>
                          {communication.decision_titre && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-500">
                              Decision : {communication.decision_titre}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-700 mt-1.5 whitespace-pre-wrap">{communication.message}</p>
                        <p className="text-[10px] text-gray-400 mt-1.5">
                          Parent : <span className="font-medium text-gray-500">{communication.destinataire_nom || communication.parent_nom || '-'}</span>
                          {communication.sent_by_nom && <> - Par <span className="font-medium text-gray-500">{communication.sent_by_nom}</span></>}
                        </p>
                      </div>
                    </div>
                  );
                }

                const action = item.action;
                const typeConfig = {
                  contact_parent: { color: '#0284c7', bg: '#e0f2fe', label: 'Contact parent', emoji: '' },
                  convocation: { color: '#dc2626', bg: '#fee2e2', label: 'Convocation', emoji: '📋' },
                  recommandation: { color: '#ea580c', bg: '#ffedd5', label: 'Recommandation', emoji: '💡' },
                  observation: { color: '#7c3aed', bg: '#ede9fe', label: 'Observation', emoji: '👁️' },
                  sanction: { color: '#b91c1c', bg: '#fecaca', label: 'Sanction', emoji: '⚠️' },
                }[action.type] || { color: '#6b7280', bg: '#f3f4f6', label: action.type, emoji: '📝' };

                const dateFormatted = action.created_at
                  ? new Date(action.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                  : '';

                return (
                  <div key={item.id} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border-l-4 group hover:bg-gray-100 transition-colors"
                    style={{ borderLeftColor: typeConfig.color }}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold"
                          style={{ backgroundColor: typeConfig.bg, color: typeConfig.color }}>
                          {typeConfig.emoji} {typeConfig.label}
                        </span>
                        <span className="text-[10px] text-gray-400 flex items-center gap-1">
                          <Calendar size={10} /> {dateFormatted}
                        </span>
                        {action.decision_titre && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-500">
                            Decision : {action.decision_titre}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-700 mt-1.5 whitespace-pre-wrap">{action.description}</p>
                      {action.auteur_nom && (
                        <p className="text-[10px] text-gray-400 mt-1.5">Par <span className="font-medium text-gray-500">{action.auteur_nom}</span></p>
                      )}
                    </div>
                    <button onClick={() => supprimerAction(action.id)}
                      className="p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
                      title="Supprimer cette entrée">
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : !showJournalForm && (
            <div className="text-center py-8">
              <MessageSquare size={32} className="mx-auto mb-2 text-gray-300" />
              <p className="text-sm text-gray-400">Aucune action enregistrée pour cet élève</p>
              <p className="text-xs text-gray-300 mt-1">Convocations, recommandations, observations et sanctions apparaîtront ici</p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-md p-5 mt-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                <Save size={18} className="text-[#7c3aed]" /> Decisions direction
              </h3>
              <p className="text-xs text-gray-400 mt-1">
                Enregistre une decision globale pour l eleve ou une decision ciblee sur un probleme precis.
              </p>
            </div>
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
              <CheckCircle size={13} /> {decisionsDirection.length} decision{decisionsDirection.length > 1 ? 's' : ''}
            </span>
          </div>

          {decisionsDirection.length > 0 && (
            <div className="space-y-2 mb-5">
              {decisionsDirection.map((decision) => {
                const followUp = getDecisionFollowUpState(decision);
                const needsFollowUp = decisionNeedsFollowUp(decision);

                return (
                  <div key={decision.id} className={`rounded-lg border p-3 ${needsFollowUp && !followUp.hasFollowUp
                    ? 'border-amber-200 bg-amber-50/60'
                    : 'border-gray-200 bg-gray-50'
                    }`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${decision.portee === 'ciblee' ? 'bg-amber-50 text-amber-700' : 'bg-sky-50 text-sky-700'
                            }`}>
                            {decision.portee === 'ciblee' ? 'Ciblee' : 'Globale'}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${decision.statut === 'cloturee' ? 'bg-slate-200 text-slate-600' : 'bg-emerald-50 text-emerald-700'
                            }`}>
                            {decision.statut === 'cloturee' ? 'Cloturee' : 'Active'}
                          </span>
                          {decision.source_type && decision.source_type !== 'synthese' && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-50 text-[#7c3aed]">
                              Source : {DECISION_SOURCE_LABELS[decision.source_type] || decision.source_type}
                              {decision.source_id ? ` #${decision.source_id}` : ''}
                            </span>
                          )}
                          {needsFollowUp && (
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${followUp.hasFollowUp
                              ? 'bg-emerald-50 text-emerald-700'
                              : 'bg-amber-100 text-amber-700'
                              }`}>
                              {followUp.hasFollowUp ? 'Suivi engage' : 'Plan/action attendu'}
                            </span>
                          )}
                          <span className="text-sm font-semibold text-gray-800">
                            {decision.titre_probleme || 'Decision direction'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {decision.decision_finale_label || decision.decision_finale} - {formatDateTime(decision.updated_at || decision.decided_at)}
                        </p>
                        {decision.commentaire_direction && (
                          <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{decision.commentaire_direction}</p>
                        )}
                        {decision.cloture_commentaire && (
                          <p className="text-xs text-slate-500 mt-2 bg-white rounded p-2 border border-slate-200">
                            Cloture : {decision.cloture_commentaire}
                          </p>
                        )}
                        {followUp.activePlans.length > 0 && (
                          <p className="text-xs text-amber-700 mt-2 bg-white rounded p-2 border border-amber-100">
                            {followUp.activePlans.length} plan{followUp.activePlans.length > 1 ? 's' : ''} encore ouvert{followUp.activePlans.length > 1 ? 's' : ''}.
                          </p>
                        )}
                      </div>
                      {decision.statut !== 'cloturee' && (
                        <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
                          {needsFollowUp && !followUp.hasFollowUp && (
                            <button
                              type="button"
                              onClick={() => ouvrirPlanPourDecision(decision)}
                              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white border border-amber-200 text-amber-700 hover:bg-amber-50 transition-colors"
                            >
                              Creer plan
                            </button>
                          )}
                          <button
                            onClick={() => cloturerDecisionDirection(decision)}
                            disabled={decisionClosingId === decision.id}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white border border-gray-200 text-gray-600 hover:border-[#7c3aed]/40 hover:bg-purple-50 transition-colors disabled:opacity-50"
                          >
                            {decisionClosingId === decision.id ? 'Cloture...' : 'Cloturer'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {decisionsSansSuivi.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h4 className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                    <AlertTriangle size={16} /> Suivi post-decision attendu
                  </h4>
                  <p className="text-xs text-amber-700 mt-1">
                    Ces decisions sont actives mais aucun plan, journal ou contact parent n est encore rattache.
                  </p>
                </div>
                <span className="px-2.5 py-1 rounded-full bg-white text-amber-700 text-[11px] font-semibold border border-amber-100">
                  {decisionsSansSuivi.length}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {decisionsSansSuivi.map((decision) => (
                  <button
                    key={decision.id}
                    type="button"
                    onClick={() => ouvrirPlanPourDecision(decision)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-amber-200 text-xs font-semibold text-amber-700 hover:bg-amber-100 transition-colors"
                  >
                    <Plus size={13} />
                    Plan pour {decision.titre_probleme || decision.decision_finale_label || 'decision'}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-xl border border-sky-100 bg-sky-50/40 p-4 mb-5">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                  <Target size={16} className="text-sky-600" /> Plan d'accompagnement
                </h4>
                <p className="text-xs text-gray-400 mt-1">
                  Actions concretes retenues par la direction, avec responsable, echeance et statut.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="hidden sm:inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white text-sky-700 border border-sky-100">
                  {plansActifsCount} actif{plansActifsCount > 1 ? 's' : ''}
                </span>
                {!showPlanForm && (
                  <button
                    type="button"
                    onClick={() => {
                      resetPlanForm(decisionDirection?.id || '');
                      setShowPlanForm(true);
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-sky-600 text-white rounded-lg text-xs font-semibold hover:bg-sky-700 transition-colors"
                  >
                    <Plus size={14} /> Ajouter
                  </button>
                )}
              </div>
            </div>

            {plans.length > 0 ? (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 mb-4">
                {plans.map((plan) => {
                  const statusStyle = PLAN_STATUS_STYLE[plan.statut] || PLAN_STATUS_STYLE.a_faire;
                  const priorityStyle = PLAN_PRIORITY_OPTIONS.find(option => option.value === plan.priorite) || PLAN_PRIORITY_OPTIONS[1];
                  const isClosedPlan = ['termine', 'annule'].includes(plan.statut);

                  return (
                    <div key={plan.id} className="rounded-lg border border-white bg-white p-3 shadow-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusStyle.className}`}>
                              {statusStyle.label}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${priorityStyle.className}`}>
                              {priorityStyle.label}
                            </span>
                            {(plan.decision_titre || plan.decision_finale_label) && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-500">
                                {plan.decision_titre || plan.decision_finale_label}
                              </span>
                            )}
                          </div>
                          <p className="text-sm font-semibold text-gray-800 whitespace-pre-wrap">{plan.objectif}</p>
                          {plan.resultats_attendus && (
                            <p className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">Resultat attendu : {plan.resultats_attendus}</p>
                          )}
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-400 mt-2">
                            {plan.responsable_nom && <span>Responsable : <b className="text-gray-500">{plan.responsable_nom}</b></span>}
                            {plan.echeance && <span>Echeance : <b className="text-gray-500">{new Date(plan.echeance).toLocaleDateString('fr-FR')}</b></span>}
                          </div>
                          <div className="text-[10px] text-gray-400 mt-2">
                            {plan.created_by_nom && <>Cree par <span className="font-medium text-gray-500">{plan.created_by_nom}</span></>}
                            {plan.completed_by_nom && <> - Termine par <span className="font-medium text-gray-500">{plan.completed_by_nom}</span></>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {!isClosedPlan && (
                            <>
                              <button
                                type="button"
                                onClick={() => modifierPlanAccompagnement(plan)}
                                className="px-2 py-1 rounded-md text-[11px] font-semibold text-sky-700 hover:bg-sky-50 transition-colors"
                              >
                                Modifier
                              </button>
                              <button
                                type="button"
                                onClick={() => terminerPlanAccompagnement(plan)}
                                disabled={planCompletingId === plan.id}
                                className="px-2 py-1 rounded-md text-[11px] font-semibold text-emerald-700 hover:bg-emerald-50 transition-colors disabled:opacity-50"
                              >
                                {planCompletingId === plan.id ? '...' : 'Terminer'}
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            onClick={() => supprimerPlanAccompagnement(plan.id)}
                            className="p-1.5 rounded-md text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                            title="Supprimer le plan"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : !showPlanForm && (
              <div className="text-center py-5 mb-2">
                <Target size={28} className="mx-auto mb-2 text-sky-200" />
                <p className="text-sm text-gray-400">Aucun plan d accompagnement defini.</p>
              </div>
            )}

            {showPlanForm && (
              <div className="rounded-xl border border-sky-200 bg-white p-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <p className="text-sm font-semibold text-sky-700">
                    {editingPlanId ? 'Modifier le plan' : 'Nouveau plan'}
                  </p>
                  {editingPlanId && (
                    <span className="px-2 py-1 rounded-full text-[10px] font-semibold bg-sky-50 text-sky-700">
                      Edition
                    </span>
                  )}
                </div>

                {decisionsDirection.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-semibold text-gray-600 mb-2">Decision rattachee</p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setPlanForm(prev => ({ ...prev, decision_direction_id: '' }))}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${!planForm.decision_direction_id
                          ? 'bg-sky-600 border-sky-600 text-white'
                          : 'bg-white border-gray-200 text-gray-500 hover:border-sky-300'
                          }`}
                      >
                        General
                      </button>
                      {decisionsDirection.map((decision) => (
                        <button
                          key={decision.id}
                          type="button"
                          onClick={() => setPlanForm(prev => ({ ...prev, decision_direction_id: decision.id }))}
                          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${Number(planForm.decision_direction_id) === Number(decision.id)
                            ? 'bg-sky-600 border-sky-600 text-white'
                            : 'bg-white border-gray-200 text-gray-500 hover:border-sky-300'
                            }`}
                        >
                          {decision.titre_probleme || decision.decision_finale_label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                  <div className="lg:col-span-2">
                    <label className="block text-xs font-semibold text-gray-600 mb-2">Objectif</label>
                    <textarea
                      value={planForm.objectif}
                      onChange={(e) => setPlanForm(prev => ({ ...prev, objectif: e.target.value }))}
                      rows={3}
                      placeholder="Ex: Stabiliser les absences sur les deux prochaines semaines..."
                      className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 outline-none transition-all resize-none"
                    />
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-2">Responsable</label>
                      <input
                        type="text"
                        value={planForm.responsable_nom}
                        onChange={(e) => setPlanForm(prev => ({ ...prev, responsable_nom: e.target.value }))}
                        placeholder="Direction, surveillant, parent..."
                        className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-2">Echeance</label>
                      <input
                        type="date"
                        value={planForm.echeance}
                        onChange={(e) => setPlanForm(prev => ({ ...prev, echeance: e.target.value }))}
                        className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 outline-none transition-all"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">Priorite</label>
                    <div className="grid grid-cols-3 gap-2">
                      {PLAN_PRIORITY_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setPlanForm(prev => ({ ...prev, priorite: option.value }))}
                          className={`px-3 py-2 rounded-lg border text-xs font-semibold transition-all ${planForm.priorite === option.value
                            ? 'border-sky-600 bg-sky-600 text-white'
                            : `${option.className} hover:border-sky-300`
                            }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">Statut</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {PLAN_STATUS_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setPlanForm(prev => ({ ...prev, statut: option.value }))}
                          className={`px-3 py-2 rounded-lg border text-xs font-semibold transition-all ${planForm.statut === option.value
                            ? 'border-sky-600 bg-sky-600 text-white'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-sky-300'
                            }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">Resultats attendus</label>
                    <textarea
                      value={planForm.resultats_attendus}
                      onChange={(e) => setPlanForm(prev => ({ ...prev, resultats_attendus: e.target.value }))}
                      rows={3}
                      placeholder="Critere qui permettra de juger que le plan fonctionne..."
                      className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 outline-none transition-all resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">Commentaire</label>
                    <textarea
                      value={planForm.commentaire}
                      onChange={(e) => setPlanForm(prev => ({ ...prev, commentaire: e.target.value }))}
                      rows={3}
                      placeholder="Details, conditions, point d attention..."
                      className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 outline-none transition-all resize-none"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 mt-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPlanForm(false);
                      resetPlanForm(decisionDirection?.id || '');
                    }}
                    className="px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={enregistrerPlanAccompagnement}
                    disabled={planSubmitting || !planForm.objectif.trim()}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-sky-600 text-white rounded-lg text-xs font-semibold hover:bg-sky-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {planSubmitting ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div> : <Save size={13} />}
                    {editingPlanId ? 'Enregistrer' : 'Ajouter le plan'}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-purple-100 bg-purple-50/40 p-4">
            {decisionSourceContext && (
              <div className="rounded-lg border border-purple-200 bg-white p-3 mb-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-50 text-[#7c3aed] border border-purple-100">
                        Source Vie scolaire
                      </span>
                      {decisionSourceContext.severity && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-50 text-red-600">
                          {decisionSourceContext.severity}
                        </span>
                      )}
                      {decisionSourceContext.date && (
                        <span className="text-[10px] text-gray-400">{formatDateTime(decisionSourceContext.date)}</span>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-gray-800">{decisionSourceContext.title}</p>
                    {decisionSourceContext.description && (
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2 whitespace-pre-wrap">{decisionSourceContext.description}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={resetDecisionForm}
                    className="px-2.5 py-1 rounded-md text-[11px] font-semibold text-gray-500 hover:bg-gray-100 transition-colors flex-shrink-0"
                  >
                    Retirer
                  </button>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-2">Portee</label>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {[
                    { value: 'globale', label: 'Globale' },
                    { value: 'ciblee', label: 'Ciblee' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        if (option.value === 'globale') {
                          setDecisionSourceContext(null);
                          setDecisionForm(prev => ({
                            ...prev,
                            portee: 'globale',
                            source_type: 'synthese',
                            source_id: '',
                            titre_probleme: '',
                          }));
                          return;
                        }
                        setDecisionForm(prev => ({ ...prev, portee: option.value }));
                      }}
                      className={`px-3 py-2 rounded-lg border text-xs font-semibold transition-all ${decisionForm.portee === option.value
                        ? 'border-[#7c3aed] bg-[#7c3aed] text-white shadow-sm'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-[#7c3aed]/40'
                        }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                <label className="block text-xs font-semibold text-gray-600 mb-2">Decision finale</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {DECISION_DIRECTION_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setDecisionForm(prev => ({ ...prev, decision_finale: option.value }))}
                      className={`px-3 py-2 rounded-lg border text-xs font-semibold text-left transition-all ${decisionForm.decision_finale === option.value
                        ? 'border-[#7c3aed] bg-[#7c3aed] text-white shadow-sm'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-[#7c3aed]/40 hover:bg-purple-50'
                        }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-2">
                {decisionForm.portee === 'ciblee' && (
                  <div className="mb-4">
                    <label className="block text-xs font-semibold text-gray-600 mb-2">Probleme cible</label>
                    <input
                      type="text"
                      value={decisionForm.titre_probleme}
                      onChange={(e) => setDecisionForm(prev => ({ ...prev, titre_probleme: e.target.value }))}
                      placeholder="Ex: Absences repetees, incident du 12/05, baisse en mathematiques..."
                      className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-[#7c3aed] focus:ring-4 focus:ring-[#7c3aed]/10 outline-none transition-all"
                    />
                    <div className="flex flex-wrap gap-2 mt-2">
                      {[
                        { value: 'vie_scolaire', label: 'Vie scolaire' },
                        { value: 'matiere', label: 'Matiere' },
                        { value: 'absence', label: 'Absence' },
                        { value: 'comportement', label: 'Comportement' },
                        { value: 'autre', label: 'Autre' },
                      ].map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => {
                            setDecisionSourceContext(null);
                            setDecisionForm(prev => ({ ...prev, source_type: option.value, source_id: '' }));
                          }}
                          className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${decisionForm.source_type === option.value
                            ? 'bg-[#7c3aed] border-[#7c3aed] text-white'
                            : 'bg-white border-gray-200 text-gray-500 hover:border-[#7c3aed]/40'
                            }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <label className="block text-xs font-semibold text-gray-600 mb-2">Commentaire direction</label>
                <textarea
                  value={decisionForm.commentaire_direction}
                  onChange={(e) => setDecisionForm(prev => ({ ...prev, commentaire_direction: e.target.value }))}
                  rows={5}
                  placeholder="Solution retenue, conditions, accompagnement prevu..."
                  className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-[#7c3aed] focus:ring-4 focus:ring-[#7c3aed]/10 outline-none transition-all resize-none"
                />
                <div className="flex items-center justify-end mt-3">
                  <button
                    onClick={enregistrerDecisionDirection}
                    disabled={decisionSaving || !decisionForm.decision_finale || (decisionForm.portee === 'ciblee' && !decisionForm.titre_probleme.trim())}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#7c3aed] text-white rounded-lg text-sm font-semibold hover:bg-[#6d28d9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {decisionSaving ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Save size={15} />}
                    Ajouter la decision
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // RENDER PRINCIPAL
  // ============================================

  if (!activeYear) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7c3aed]"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-gradient-to-r from-[#7c3aed] to-[#6d28d9] rounded-xl shadow-lg p-6 text-white mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold mb-1">Outil Décisionnel</h1>
            <p className="text-sm text-white/70">Suivi & pilotage pédagogique des élèves</p>
          </div>
          <div className="flex items-center gap-3">

            <button
              type="button"
              onClick={() => setIsGuideOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/15 text-white text-xs font-semibold hover:bg-white/25 transition-colors"
            >
              <BookOpen size={15} />
              Guide d'utilisation
            </button>

          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 mb-6 flex items-center gap-3">
          <AlertTriangle size={20} className="text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600 text-lg">✕</button>
        </div>
      )}

      {vue !== 'fiche' && renderFiltres()}
      {vue === 'dashboard' && renderDashboard()}
      {vue === 'fiche' && renderFicheEleve()}

      {ficheLoadingEleveId && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-slate-950/35 p-4 backdrop">
          <div className="flex items-center gap-3 rounded-xl border border-purple-100 bg-white px-5 py-4 shadow-2xl">
            <div className="h-7 w-7 rounded-full border-2 border-[#7c3aed]/25 border-b-[#7c3aed] animate-spin" />
            <div>
              <p className="text-sm font-semibold text-slate-900">Ouverture de la fiche</p>
              <p className="text-xs text-slate-500">Chargement des données décisionnelles...</p>
            </div>
          </div>
        </div>
      )}

      {confirmDialog && (
        <ConfirmDialog
          isOpen={Boolean(confirmDialog)}
          onClose={() => {
            setConfirmDialog(null);
            setDecisionCloseComment('');
          }}
          onConfirm={handleConfirmAction}
          title={confirmDialog.title}
          description={confirmDialog.description}
          confirmText={confirmDialog.confirmText}
          cancelText="Annuler"
          type={confirmDialog.type || 'warning'}
          isLoading={confirmLoading}
          confirmDisabled={Boolean(confirmDialog.requireCloseComment) && !decisionCloseComment.trim()}
        >
          {confirmDialog.requireCloseComment && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2">
                Solution trouvee <span className="text-red-500">*</span>
              </label>
              <textarea
                value={decisionCloseComment}
                onChange={(e) => setDecisionCloseComment(e.target.value)}
                rows={4}
                placeholder="Ex: Parents rencontres, engagement signe, plan termine, situation regularisee..."
                className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-[#7c3aed] focus:ring-4 focus:ring-[#7c3aed]/10 outline-none transition-all resize-none"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Cette note sera conservee dans l historique de la decision.
              </p>
            </div>
          )}
        </ConfirmDialog>
      )}

      {toastInfo && (
        <Toast
          message={toastInfo.message}
          type={toastInfo.type}
          onClose={() => setToastInfo(null)}
        />
      )}

      <GuideUtilisationDrawer
        isOpen={isGuideOpen}
        onClose={() => setIsGuideOpen(false)}
        module="outilDecisionnel"
      />
    </div>
  );
};

export default OutilDecisionnel;
